import React, { useState, useEffect, useContext, useRef } from "react";
import { Box, Grid, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import GPAL from "../../commons/GPAL";
import Scrollbar from "./Scrollbar";
import moment from "moment";
import ObstetricHistoryCard from "./ObstetricHistoryCard";
import DiseaseSelector from "./OptionSelectionWithAddMore";
import { useSelector } from "react-redux";
import * as patientActions from '../../redux/actions/patientActions';
import { store } from '../../redux/store/index';
import { ConsultationContext,} from "../../context/ConsultationContext";
import CustomSelectBox from "../../commons/CustomSelect";
import { abortionWeeks, interPregnancyMonths, interPregnancyYears, months, preTermWeeks, termWeeks, years } from "../../commons/constants";
import { CircularProgress } from '@mui/material';
import { Autocomplete, } from '@mui/material';
import { socket } from "../../socket/socket";
import usePermissions from "../../utils/permissions";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CreatableSelect from "react-select/creatable";
import { API } from "../../api/api";
import { END_POINTS } from "../../api/endpoints";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useToast } from "../../context/ToastContext";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomTextField from "../../commons/CustomTextField";
import TableSelBox from "../../commons/TableSelBox";
import CommonGate from "./Gate";
import * as constantActions from '../../redux/actions/constantActions';

const menuStyle = {
    '&:hover': {
        backgroundColor: '#D3D3D3',
        color: 'black',
    },
    '&.Mui-selected': {
        backgroundColor: 'grey',
        color: '#fff',
    },
    '&.Mui-selected:hover': {
        backgroundColor: 'grey',
    },

    '&.Mui-focusVisible': {
        backgroundColor: 'grey',
    },
    '&.Mui-selected.Mui-focusVisible': {
        backgroundColor: 'grey',
    },
}

const boxContainer = {
    flex: 1, minHeight: 0, height: { xs: 'auto', md: '100%' }, overflow: { xs: 'visible', md: 'hidden' },
    '& .custom-scrollbars__container': {
        height: '100%'
    },
    '& .custom-scrollbars__content': {
        height: '100%',
        maxHeight: '100%'
    },
}

const gridContainer = {
    borderRadius: 4, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' }, marginBottom: '10px'
}

const headerLabel = {
    fontSize: { xl: '13px', lg: '12px', md: '11px' }
}

const yesNoContainer = {
    width: '50%',
    height: '18px',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    p: 0.6,
    borderRadius: 2,
}

const History = () => {
    const { patientId, consultationId } = useParams();
    const { setChildData } = useContext(ConsultationContext);
    const [edit, setEdit] = useState(false)
    const location = useLocation();
    const [loader, setLoader] = useState(true)
    const inputRef = useRef(null);
    const diseaseRefs = useRef({});
    const { hasPermission } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMd = useMediaQuery(theme.breakpoints.down('md'));
    const isLg = useMediaQuery(theme.breakpoints.down('lg'));
    const fontSize = isMd ? '10px' : isLg ? '11px' : '12px';
    const [lmpDate, setLmpDate] = useState(null)
    const [isEditingLmp, setIsEditingLmp] = useState(false)
    const showToast = useToast()
    const [mode, setMode] = useState('view')
    const patientReducer = useSelector((state) => state.patientReducer);
    const [blocks, setBlocks] = useState()
    const [historyBlocks, setHistoryBlocks] = useState({});
    const [create, setCreate] = useState(false)
    const patientType = patientReducer?.patientInfo?.patientType || 'OBSTETRIC'

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const mode = queryParams.get('mode');
        if (mode) {
            setMode(mode);
        }
    }, [location.search]);

    const getHistoryData = async () => {
        try {
            const res = await API.get(
                `/v1/patients/${patientId}/history/blocks`
            );
            const mapped = {};
            res.data.blocks.forEach(block => {
                mapped[block.code] = block;
            });
            setHistoryBlocks(mapped);
            const isEveryDataNull = res.data.blocks.every(item => item.data === null)
            setCreate(isEveryDataNull ? true : false)
            setChildData({ ...mapped, id: create })
            setEdit(isEveryDataNull ?  mode == "edit" ? true : false : hasPermission("HISTORY_CREATE") ? mode == "edit" ? true : false : false)
        }
        catch (e) {
            console.log("Er", e)
        }
    }

    useEffect(() => {
        setEdit(
            mode === "edit" &&
            (create || hasPermission("HISTORY_CREATE"))
        );
    }, [mode, create]);

    const getHistoryBlocks = async () => {
        try {
            const res = await API.get(
                `/v1/patients/${patientId}/history-config`,
            );
            const overallBlock = res?.data?.blocks;
            setBlocks(overallBlock)
            setFamilyHistoryList(
                overallBlock.find(item => item.code === "PAST_HISTORY")
                    ?.config?.familyDiseases || []
            );
        } catch (e) {
            console.log(e);
        }
    };

    useEffect(() => {
        setHistoryBlocks({})
        setLmpDate(null);
        if (patientId) {
            getHistoryBlocks()
            getHistoryData()
        }
    }, [patientId])

    const refreshHistory = async () => {
        await Promise.all([
          getHistoryBlocks(),
          getHistoryData(),
        ]);
      };

    useEffect(() => {
        setLmpDate(null);
        if (patientId) {
            store.dispatch(constantActions.getHistoryConstantRequestAction());
        }
    }, [patientId])

    useEffect(() => {
        if (!patientId) return;
        const handleCommandOutput = (data) => {
            console.log("data came on event", data)
            const [, , rawCardType] = data?.data?.originActionType?.split('.');

            let cardType = rawCardType;
            let cardKey = rawCardType;
            if (rawCardType.includes(':')) {
                const [cardCategory, id] = rawCardType.split(':');
                cardType = cardCategory
                cardKey = rawCardType
            }
            if (consultationId !== data?.data?.consultationId) {
                return
            }
            if (
                data?.actionType === "commandOutput.refreshWebDashboard"
            ) {
                if (cardType == "pregnancyConfirmation" || cardType == "termHistory" || cardType == "preTermHistory" || cardType == "abortionHistory" || cardType == "gtpla" || cardType == "pastDiseaseHistory" || cardType == "pastMedicalHistory" || cardType == "currentPregnancy" || cardType == "familyHistory" ||
                    cardType == "developmentalHistory" || cardType == "metabolicFollowupHistory" || cardType == "moodScreenHistory" || cardType == "contraceptionHistory" || cardType == "cancerFamilyHistory" || cardType == "vulvalSymptomHistory" || cardType == "menopauseSymptomHistory" || cardType == "thrombosisAutoimmuneHistory" ||
                    cardType == "pregnancyLossHistory" || cardType == "fertilityHistory" || cardType == "painSymptomHistory" || cardType == "androgenicMetabolicHistory" || cardType == "condition"||cardType == "conditions"
                ) {
                    refreshHistory()
                }
            }
        };
        socket.on("commandOutput", handleCommandOutput);
    }, [patientId]);

    const constantReducer = useSelector((state) => state.constantReducer);

    useEffect(() => {
        setLmpDate(patientReducer?.patientInfo?.lmp)
    }, [patientReducer?.patientInfo])

    useEffect(() => {
        if (patientReducer?.conditionCreated?.consultationId) {
            store.dispatch(patientActions.resetConditionCreation())
            // store.dispatch(patientActions.getPatientDetailsRequestAction(patientId));
            store.dispatch(patientActions.getConditionTimelineRequestAction(patientId));
            getHistoryBlocks()
            getHistoryData()
        }
    }, [patientReducer?.conditionCreated])

    useEffect(() => {
        setChildData({ ...historyBlocks, id: create })
    }, [historyBlocks]);

    const [familyHistoryList, setFamilyHistoryList] = useState([]);

    const updateObstetricsHistories = (code, value, historyType) => {
        setHistoryBlocks(prev => {
            const updated = structuredClone(prev);

            const data = updated[code].data;

            if (!data.obstetricHistories) {
                data.obstetricHistories = [];
            }

            if (value === 0) {
                data.obstetricHistories =
                    data.obstetricHistories.filter(
                        item => item.historyType !== historyType
                    );

                return updated;
            }

            const currentCount =
                data.obstetricHistories.filter(
                    item => item.historyType === historyType
                ).length;

            if (value > currentCount) {
                for (let i = 0; i < value - currentCount; i++) {
                    data.obstetricHistories.push({
                        historyType
                    });
                }
            } else if (value < currentCount) {
                let remaining = value;

                data.obstetricHistories =
                    data.obstetricHistories.filter(item => {
                        if (item.historyType !== historyType) {
                            return true;
                        }

                        if (remaining > 0) {
                            remaining--;
                            return true;
                        }

                        return false;
                    });
            }

            return updated;
        });
    };


    const changeSingleChoiceOption = (
        code,
        block,
        index,
        field,
        data
    ) => {
        setHistoryBlocks(prev => {
            const updated = structuredClone(prev);

            if (!updated[code].data) {
                updated[code].data = {};
            }

            if (!updated[code].data[block]) {
                updated[code].data[block] = [];
            }

            if (!updated[code].data[block][index]) {
                updated[code].data[block][index] = {};
            }

            if (!Array.isArray(updated[code].data[block][index][field])) {
                updated[code].data[block][index][field] = [];
            }

            const current =
                updated[code].data[block][index][field];

            const exists = current.some(
                item => item.label === data.label
            );

            updated[code].data[block][index][field] = exists
                ? current.filter(item => item.label !== data.label)
                : [...current, data];

            return updated;
        });
    };
    const range = (start, end) => {
        return Array(end - start + 1)
            .fill()
            .map((_, idx) => start + idx);
    };
    console.log("hisot", historyBlocks)
    const handleChangeObstetricsData = (
        code,
        block,
        index,
        field,
        value
    ) => {
        setHistoryBlocks(prev => {
            const updated = structuredClone(prev);

            if (!updated[code].data) {
                updated[code].data = {};
            }

            if (!updated[code].data[block]) {
                updated[code].data[block] = [];
            }

            if (!updated[code].data[block][index]) {
                updated[code].data[block][index] = {};
            }

            updated[code].data[block][index][field] = value;

            return updated;
        });
    };

    const handleChangeObstetricsParentChildren = (
        code,
        block,
        index,
        field,
        innerField,
        value,
        label
    ) => {
        setHistoryBlocks(prev => {
            const updated = structuredClone(prev);

            if (!updated[code].data) {
                updated[code].data = {};
            }

            if (!updated[code].data[block]) {
                updated[code].data[block] = [];
            }

            if (!updated[code].data[block][index]) {
                updated[code].data[block][index] = {};
            }

            if (!updated[code].data[block][index][field]) {
                updated[code].data[block][index][field] = {};
            }

            if (innerField === "label") {
                updated[code].data[block][index][field] = {
                    label,
                    value,
                    children: [],
                };
            } else {
                updated[code].data[block][index][field][innerField] = value;
            }

            return updated;
        });
    };

    const handleFamilyHistory = async (data, code) => {
        const diseases = historyBlocks[code]?.data?.familyHistories || [];

        let disease = {
            diseaseId: data.id,
            diseaseName: data.name,
            familyMembers: [],
        };

        if (data.__isNew__) {
            const response = await API.post(
                END_POINTS.createData,
                { name: data.name }
            );

            disease = {
                diseaseId: response.data.id,
                diseaseName: response.data.name,
                familyMembers: [],
            };

            setFamilyHistoryList(prev => [
                {
                    id: response.data.id,
                    name: response.data.name,
                },
                ...prev,
            ]);
        }

        const exists = diseases.some(
            d => d.diseaseId === disease.diseaseId
        );

        const updatedDiseases = exists
            ? diseases.filter(d => d.diseaseId !== disease.diseaseId)
            : [...diseases, disease];

        updateHistoryBlock(code, ["familyHistories"], updatedDiseases);
    };

    const sortedHistory = (data) => {
        return data?.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB.getFullYear() - dateA.getFullYear() ||
                dateB.getMonth() - dateA.getMonth();
        });
    }

    const updateDonorChildren = (tree, targetValue, newChildren) => {
        return tree.map(node => {
            if (node.value === targetValue) {
                return {
                    ...node,
                    children: newChildren,
                };
            } else if (node.children && node.children.length > 0) {
                return {
                    ...node,
                    children: updateDonorChildren(node.children, targetValue, newChildren),
                };
            } else {
                return node;
            }
        });
    };

    useEffect(() => {
        if (constantReducer?.historyConfig) {
            const timeout = setTimeout(() => {
                setLoader(false);
            }, 500);

            return () => clearTimeout(timeout);
        }
    }, [constantReducer?.historyConfig]);

    const getLeftSpace = (value) => {
        return value == "LIVE_BIRTH" ? 2 : value == "STILLBIRTH" ? 2.8 : value == "NEONATAL_DEATH" ? 4 : 6
    }

    const formatText = (text) => {
        return text.toLowerCase()
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
    }

    const updateHistoryBlock = (code, path, value) => {
        setHistoryBlocks(prev => {
            const updated = structuredClone(prev);

            if (!updated[code].data) {
                updated[code].data = {};
            }

            let obj = updated[code].data;

            for (let i = 0; i < path.length - 1; i++) {
                if (obj[path[i]] == null) {
                    obj[path[i]] = {};
                }

                obj = obj[path[i]];
            }

            obj[path[path.length - 1]] = value;

            return updated;
        });
    };

    const showMenstrualHistory = (config, code) => {
        return <Grid item sx={{ padding: 1, pb: 2 }}>
            {/* Menstrual History Section */}
            {/* <Typography textAlign={"left"} sx={{ fontSize: { xl: '13px', lg: '12px', md: '12px', } }}>Menstrual History</Typography> */}
            <Grid container spacing={1} sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)',
                p: 1,
                borderRadius: 4,
            }}>
                {/* Column Labels */}
                <Grid size={{ xs: 0, md: 3, }} sx={{ display: { xs: 'none', md: 'block' } }}></Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                    <Typography align="center" sx={{ fontSize: { xl: '13px', lg: '12px', md: '11px' } }}>
                        Current
                    </Typography>
                </Grid>

                <Grid size={{ xs: 6, md: 2, }}>
                    <Typography align="center" sx={{ fontSize: { xl: '13px', lg: '12px', md: '11px' } }}>
                        Past
                    </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 5 }} />
                {/* Shared dropdown sx */}
                {[
                    {
                        label: 'Cycle Days',
                        currentValue: historyBlocks?.[code]?.data?.currentMenstrualHistory?.cycleDays,
                        pastValue: historyBlocks?.[code]?.data?.pastMenstrualHistory?.cycleDays,
                        onChangeCurrent: (e) => updateHistoryBlock(
                            code,
                            ["currentMenstrualHistory", "cycleDays"],
                            parseInt(e.target.value)
                        ),
                        onChangePast: (e) => updateHistoryBlock(
                            code,
                            ["pastMenstrualHistory", "cycleDays"],
                            parseInt(e.target.value)
                        )
                        ,
                        options: range(1, 10),
                        renderOption: (item) => <MenuItem key={item} value={item} sx={menuStyle}>{item}</MenuItem>,
                    },
                    {
                        label: 'Cycle Length',
                        currentValue: historyBlocks?.[code]?.data?.currentMenstrualHistory?.cycleLength,
                        pastValue: historyBlocks?.[code]?.data?.pastMenstrualHistory?.cycleLength,
                        onChangeCurrent: (e) => updateHistoryBlock(
                            code,
                            ["currentMenstrualHistory", "cycleLength"],
                            parseInt(e.target.value)
                        ),
                        onChangePast: (e) => updateHistoryBlock(
                            code,
                            ["pastMenstrualHistory", "cycleLength"],
                            parseInt(e.target.value)
                        ),
                        options: range(21, 60),
                        renderOption: (item) => <MenuItem key={item} value={item} sx={menuStyle}>{item}</MenuItem>,
                    },
                    {
                        label: 'Nature',
                        currentValue: historyBlocks?.[code]?.data?.currentMenstrualHistory?.nature,
                        pastValue: historyBlocks?.[code]?.data?.pastMenstrualHistory?.nature,
                        onChangeCurrent: (e) => updateHistoryBlock(
                            code,
                            ["currentMenstrualHistory", "nature"],
                            e.target.value
                        ),
                        onChangePast: (e) => updateHistoryBlock(
                            code,
                            ["pastMenstrualHistory", "nature"],
                            e.target.value
                        ),
                        options: config?.natureTypes || [],
                        renderOption: (item) => <MenuItem key={item.value} value={item.value} sx={menuStyle}>{item.label}</MenuItem>,
                    },
                    {
                        label: 'Dysmenorrhea',
                        currentValue: historyBlocks?.[code]?.data?.currentMenstrualHistory?.hasDysmenorrhea === true ? 'Yes' : historyBlocks?.[code]?.data?.currentMenstrualHistory?.hasDysmenorrhea === false ? 'No' : '',
                        pastValue: historyBlocks?.[code]?.data?.pastMenstrualHistory?.hasDysmenorrhea === true ? 'Yes' : historyBlocks?.[code]?.data?.pastMenstrualHistory?.hasDysmenorrhea === false ? 'No' : '',
                        onChangeCurrent: (e) => updateHistoryBlock(
                            code,
                            ["currentMenstrualHistory", "hasDysmenorrhea"],
                            e.target.value === 'Yes'
                        ),
                        onChangePast: (e) => updateHistoryBlock(
                            code,
                            ["pastMenstrualHistory", "hasDysmenorrhea"],
                            e.target.value === 'Yes'
                        ),
                        options: ['Yes', 'No'],
                        renderOption: (item) => <MenuItem key={item} value={item} sx={menuStyle}>{item}</MenuItem>,
                    },
                    {
                        label: 'Flow',
                        currentValue: historyBlocks?.[code]?.data?.currentMenstrualHistory?.flow,
                        pastValue: historyBlocks?.[code]?.data?.pastMenstrualHistory?.flow,
                        onChangeCurrent: (e) => updateHistoryBlock(
                            code,
                            ["currentMenstrualHistory", "flow"],
                            e.target.value
                        ),
                        onChangePast: (e) => updateHistoryBlock(
                            code,
                            ["pastMenstrualHistory", "flow"],
                            e.target.value
                        ),
                        options: config?.flowTypes || [],
                        renderOption: (item) => <MenuItem key={item.value} value={item.value} sx={menuStyle}>{item.label}</MenuItem>,
                    },
                ].map(({ label, currentValue, pastValue, onChangeCurrent, onChangePast, options, renderOption }) => {
                    const dropdownSx = {
                        backgroundColor: '#FFFF',
                        '& .MuiOutlinedInput-root': {
                            // '& fieldset': { border: 'none' },
                            padding: '0px',
                            height: '27px',
                        },
                        borderRadius: '5px',
                        '& .MuiSelect-select': {
                            padding: '4px 8px',
                            fontSize: '0.75rem',
                            minHeight: 'unset',
                        },
                    };
                    return (
                        <React.Fragment key={label}>
                            <Grid size={{ xs: 12, md: 3 }} sx={{ alignSelf: 'center' }}>
                                <Typography textAlign={"left"} sx={{ fontSize: { xl: '13px', lg: '12px', md: '11px' } }}>{label}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 2 }}>
                                <TextField select size="small" fullWidth sx={dropdownSx} disabled={!edit} value={currentValue ?? ''} onChange={onChangeCurrent}>
                                    {options.map(renderOption)}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 6, md: 2 }}>
                                <TextField select size="small" fullWidth sx={dropdownSx} disabled={!edit} value={pastValue ?? ''} onChange={onChangePast}>
                                    {options.map(renderOption)}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 6, md: 5 }} />
                        </React.Fragment>
                    );
                })}
            </Grid>
        </Grid>
    }

    const showObstetricsHistory = (config, code) => {
        return <Grid item sx={{ padding: 1, pb: 2 }}>

            <Grid container justifyContent="space-between" alignItems="center" sx={{ p: 1 }}>
                <Grid size={{ xs: 12, md: 12 }} sx={{ px: { xs: 0.5, md: 0 }, mb: { xs: 1, md: 0 } }}>
                    <GPAL data={historyBlocks?.[code]?.data || {}} code={code}
                        screen="history"
                        updateHistoryBlock={updateHistoryBlock}
                        updateObstetricsHistories={updateObstetricsHistories}
                        edit={edit} />
                </Grid>
            </Grid>

            {/*  date filler for obstetics  */}

            {<Grid size={{ xs: 12, md: 12 }}>
                <Grid container spacing={{ xs: 1, md: 0 }}>
                    <Grid size={{ xs: 12, sm: 3, md: 2.5 }} sx={{ mt: 0.5 }}  >

                    </Grid>
                    {historyBlocks?.[code]?.data?.obstetricHistories?.length > 0 && edit && <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                        {historyBlocks?.[code]?.data?.obstetricHistories?.map((item, index) => {
                            return item.historyType == "TERM" &&
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, justifySelf: { xs: 'flex-start', md: 'flex-end' }, mt: 0.5 }} gap={0.5}>
                                    <Typography sx={{ alignSelf: 'center', fontSize: { xs: '12px', xl: '9px', lg: '8px', md: '8px' }, fontWeight: { xs: 600, md: 'normal' }, minWidth: { xs: 16, md: 'auto' } }}  >
                                        T
                                    </Typography>
                                    <Box sx={{
                                        backgroundColor: '#FFF', borderRadius: 2, display: 'flex', flexDirection: 'row', gap: '4px',
                                        alignItems: 'center',
                                        minWidth: 0,
                                    }}>
                                        <CustomSelectBox
                                            value={historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date ? moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date).format('MM') : ''}
                                            handleChange={(e) => {
                                                const currentYear = moment().year();
                                                const date = historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date ? moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index].date) : moment.utc(`${currentYear}-01-01T00:00:00.000Z`)
                                                date.month(parseInt(e, 10) - 1)
                                                handleChangeObstetricsData(code, "obstetricHistories", index, "date", date.toISOString())
                                            }}
                                            placeholder="MM"
                                            options={months}
                                            width={{ xs: '80px', xl: '55px', lg: '50px', md: '35px' }}
                                            edit={edit}
                                        />
                                        <CustomSelectBox
                                            value={historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date ? Number(moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date).format('YYYY')) : ''}
                                            handleChange={(e) => {
                                                const currentYear = moment().year();
                                                const date = historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date ? moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index].date) : moment.utc(`${currentYear}-01-01T00:00:00.000Z`)
                                                date.year(parseInt(e))
                                                handleChangeObstetricsData(code, "obstetricHistories", index, "date", date.toISOString())
                                            }}
                                            placeholder="YYYY"
                                            options={years}
                                            width={{ xs: '90px', xl: '65px', lg: '50px', md: '34px' }}
                                            edit={edit}
                                        />
                                    </Box>
                                </Box>
                        })}
                    </Grid>}
                    {historyBlocks?.[code]?.data?.obstetricHistories?.length > 0 && edit && <Grid size={{ xs: 12, sm: 3, md: 3 }}>
                        {historyBlocks?.[code]?.data?.obstetricHistories?.map((item, index) => {
                            return item.historyType == "PRETERM" &&
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'center' }, justifySelf: { xs: 'flex-start', md: 'center' }, mt: 0.5, ml: { xs: 0, lg: 1, md: 2 } }} gap={0.5}>
                                    <Typography sx={{ alignSelf: 'center', fontSize: { xs: '12px', xl: '9px', lg: '8px', md: '8px' }, fontWeight: { xs: 600, md: 'normal' }, minWidth: { xs: 16, md: 'auto' } }} >
                                        P
                                    </Typography>
                                    <Box sx={{ backgroundColor: '#FFF', borderRadius: 2, display: 'flex', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>
                                        <CustomSelectBox
                                            value={historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date ? moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date).format('MM') : ''}
                                            handleChange={(e) => {
                                                const currentYear = moment().year();
                                                const date = historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date ? moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date) : moment.utc(`${currentYear}-01-01T00:00:00.000Z`)
                                                date.month(parseInt(e, 10) - 1)
                                                handleChangeObstetricsData(code, "obstetricHistories", index, "date", date.toISOString())
                                            }}
                                            placeholder="MM"
                                            options={months}
                                            width={{ xs: '80px', xl: '55px', lg: '50px', md: '35px' }}
                                            edit={edit}
                                        />
                                        <CustomSelectBox
                                            value={historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date ? Number(moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date).format('YYYY')) : ''}
                                            handleChange={(e) => {
                                                const currentYear = moment().year();
                                                const date = historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date ? moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date) : moment.utc(`${currentYear}-01-01T00:00:00.000Z`)
                                                date.year(parseInt(e))
                                                handleChangeObstetricsData(code, "obstetricHistories", index, "date", date.toISOString())
                                            }}
                                            placeholder="YYYY"
                                            options={years}
                                            width={{ xs: '90px', xl: '65px', lg: '50px', md: '35px' }}
                                            edit={edit}
                                        />
                                    </Box>
                                </Box>
                        })}
                    </Grid>}

                    {historyBlocks?.[code]?.data?.obstetricHistories?.length > 0 && edit && <Grid size={{ xs: 12, sm: 3, md: 3 }}>
                        {historyBlocks?.[code]?.data?.obstetricHistories?.map((item, index) => {
                            return item.historyType == "ABORTION" &&
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: { xs: 'flex-start', md: 'flex-start' }, justifySelf: { xs: 'flex-start', md: 'flex-start' }, mt: 0.5, ml: { xs: 0, lg: 2, md: 4 } }} gap={0.5}>
                                    <Typography sx={{ alignSelf: 'center', fontSize: { xs: '12px', xl: '9px', lg: '8px', md: '8px' }, fontWeight: { xs: 600, md: 'normal' }, minWidth: { xs: 16, md: 'auto' } }} >
                                        A
                                    </Typography>
                                    <Box sx={{ backgroundColor: '#FFF', borderRadius: 2, display: 'flex', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>
                                        <CustomSelectBox
                                            value={historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date ? moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date).format('MM') : ''}
                                            handleChange={(e) => {
                                                const currentYear = moment().year();
                                                const date = historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date ? moment(historyBlocks?.[code]?.data?.obstetricHistories[index].date) : moment.utc(`${currentYear}-01-01T00:00:00.000Z`)
                                                date.month(parseInt(e, 10) - 1)
                                                handleChangeObstetricsData(code, "obstetricHistories", index, "date", date.toISOString())
                                            }}
                                            placeholder="MM"
                                            options={months}
                                            width={{ xs: '80px', xl: '55px', lg: '50px', md: '35px' }}
                                            edit={edit}
                                        />
                                        <CustomSelectBox
                                            value={historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date ? Number(moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date).format('YYYY')) : ''}
                                            handleChange={(e) => {
                                                const currentYear = moment().year();
                                                const date = historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.date ? moment(historyBlocks?.[code]?.data?.obstetricHistories[index].date) : moment.utc(`${currentYear}-01-01T00:00:00.000Z`)
                                                date.year(parseInt(e))
                                                handleChangeObstetricsData(code, "obstetricHistories", index, "date", date.toISOString())
                                            }}
                                            placeholder="YYYY"
                                            options={years}
                                            width={{ xs: '90px', xl: '65px', lg: '50px', md: '35px' }}
                                            edit={edit}
                                        />
                                    </Box>
                                </Box>
                        })}
                    </Grid>}
                </Grid>
            </Grid>}

            {/*   started obstetrics */}

            {
                sortedHistory(historyBlocks?.[code]?.data?.obstetricHistories)?.map((item, index) => {
                    const selectedNeonatal =
                        historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.neonatalOutcome;

                    const selectedItem = config?.neonatalOutcomeTypes?.find(
                        (item) => item.value === selectedNeonatal?.value
                    );
                    return item.historyType == "PRETERM" ?
                        edit ? <Grid container sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)', p: 1, mt: 1, borderRadius: 5 }} >
                            <Grid size={{ xs: 12, md: 10, }} mt={1} >
                                <Typography textAlign={'left'} fontSize={"17px"} sx={{ fontSize: { xl: '15px', lg: '13px', md: '11px' } }}>
                                    Pre Term Birth
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 2, }} mt={1} >
                                <Typography fontSize={"18px"} sx={{ fontSize: { xl: '18px', lg: '16px', md: '13px' } }}>
                                    {historyBlocks?.[code]?.data?.obstetricHistories?.[index].date ? moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index].date).format('MMM YYYY') : ''}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 12, }} mt={0.5} >
                                <Grid container>
                                    {/* Week Of Delivery */}
                                    <Grid size={{ xs: 12, md: 6, }} sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>
                                        <Grid size={{ xs: 6, md: 5, }}  >
                                            <Typography textAlign={'left'} fontSize={"12px"} sx={headerLabel} >
                                                Week Of Delivery
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 0, md: 1, }} sx={{ display: { xs: 'none', md: 'block' } }} />
                                        <Grid size={{ xs: 6, md: 6, }} >
                                            <Box sx={{ backgroundColor: '#FFF', borderRadius: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', width: { xs: '120px', md: '48%' }, px: 1 }}>
                                                <CustomSelectBox
                                                    value={item.week || ''}
                                                    handleChange={(e) => {
                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "week", parseInt(e))
                                                    }}
                                                    placeholder="00"
                                                    options={preTermWeeks}
                                                    width={'60px'}
                                                    selectStyle={{
                                                        height: '25px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px:'3px'
                                                    }}
                                                    edit={edit}
                                                />
                                                <Typography textAlign={'center'} alignSelf={'center'} fontSize={"12px"} sx={{ fontSize: { xl: '12px', lg: '11px', md: '9px' }, ml:0.5 }}>Wk</Typography>
                                            </Box>

                                        </Grid>
                                    </Grid>

                                    {/*  place of delivery */}

                                    <Grid size={{ xs: 12, md: 6, }} sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'row' }, gap: { xs: 0.5, md: 0 } }} mt={1}>
                                        <Grid size={{ xs: 6, md: 5.5, }} >
                                            <Typography textAlign={'left'} fontSize={"12px"} sx={headerLabel}>
                                                Place Of Delivery
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 0, md: 0, }} sx={{ display: { xs: 'none', md: 'block' } }}>
                                        </Grid>

                                        <Grid size={{ xs: 6, md: 6.5, }}>
                                            <Box sx={{ backgroundColor: '#FFF', borderRadius: 4, display: 'flex', flexDirection: 'row', width: { xl: '50%', lg: '70%', md: '100%' } }}>

                                                <Autocomplete
                                                    options={constantReducer.cities}
                                                    value={item.place || null}
                                                    onChange={(event, newValue) => {
                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "place", newValue);
                                                    }}
                                                    size="small"
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    '& fieldset': {
                                                                        border: '1px solid #919191',
                                                                    },
                                                                    padding: '0px',
                                                                    //   height: '25px',
                                                                    '& input': {
                                                                        padding: '6px 8px',
                                                                        fontSize: '0.75rem',
                                                                        lineHeight: 1,
                                                                    },
                                                                },
                                                                width: '100%',
                                                            }}
                                                        />
                                                    )}
                                                    sx={{
                                                        width: '100%',
                                                        '& .MuiAutocomplete-inputRoot': {
                                                            padding: '0px !important',
                                                            fontSize: '0.75rem',
                                                        },
                                                    }}
                                                    disableClearable
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 12 }} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0.5, md: 0 } }} mt={1}>
                                        <Grid size={{ xs: 12, md: 2 }}>
                                            <Typography textAlign={"left"} fontSize={"12px"} sx={headerLabel}>Delivery Type</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 10 }}>
                                            <Grid container sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: { xs: 0.5, md: 0 } }}>
                                                <Grid size={{ xs: 0, md: 2 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                                                </Grid>
                                                {
                                                    config?.preTermDeliveryTypes?.map(item => {
                                                        return <Grid size={{ xs: 'auto', md: 3 }} >
                                                            <Typography sx={{
                                                                cursor: 'pointer',
                                                                border: item.label == historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.type?.label ? '1px solid #919191' : 'none',
                                                                borderRadius: 2, p: 0.5,
                                                                fontSize: { xl: '14px', lg: '12px', md: '11px' }
                                                            }}
                                                                // fontSize={"12px"}
                                                                onClick={() => handleChangeObstetricsParentChildren(code, "obstetricHistories", index, "type", "label", item.value, item.label)
                                                                }
                                                            >
                                                                {item.label}
                                                            </Typography>

                                                        </Grid>
                                                    })
                                                }
                                            </Grid>

                                        </Grid>
                                    </Grid>


                                    <Grid size={{ xs: 12, md: 12 }} >
                                        <Grid container sx={{ flexWrap: 'wrap', gap: { xs: 0.5, md: 0 }, justifyContent: { xs: 'flex-start', md: 'flex-start' } }}>
                                            {config?.preTermDeliveryTypes?.map(item => {
                                                return item.value == historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.type?.value && item.children.map((child) => (
                                                    <Grid size={{ xs: 'auto', md: 2 }} mt={1}>
                                                        <Typography fontSize="14px"
                                                            sx={{
                                                                cursor: 'pointer',
                                                                border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.type?.children.find((it) => it.children.find((und) => und.label == child.label)) ? '1px solid #919191' : 'none',
                                                                justifySelf: item.value == "CESAREAN_SECTION" ? "flex-end" : 'flex-start',
                                                                borderRadius: 2,
                                                                p: 0.5,
                                                                fontSize: { xl: '14px', lg: '12px', md: '11px' }
                                                            }}
                                                            onClick={() => {
                                                                const findingChild = item.children.find((child) => child.value == "PRETERM")
                                                                const children = [{ ...findingChild, ["children"]: [child] }]
                                                                handleChangeObstetricsParentChildren(code, "obstetricHistories", index, "type", "children", children)
                                                            }}
                                                        >
                                                            {child.label}
                                                        </Typography>

                                                    </Grid>
                                                ))
                                            })}

                                            {historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.type?.value && <Box sx={{ display: { xs: 'none', md: 'inline-block' }, lineHeight: 0 }}><svg xmlns="http://www.w3.org/2000/svg"
                                                width="40"
                                                height="50"
                                                viewBox="80 50 140 250"
                                            >
                                                <path
                                                    d="M10 100 Q 100 0, 190 140"
                                                    stroke="black"
                                                    strokeWidth="5"
                                                    fill="transparent"
                                                    transform="rotate(135 100 100)"
                                                />
                                            </svg></Box>}

                                        </Grid>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 12 }} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>
                                        {historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.type?.value == "CESAREAN_SECTION" &&
                                            <>
                                                <Grid size={{ xs: 12, md: 2 }} mt={1} >

                                                    <Typography fontSize={"13px"} sx={{ fontSize: { xl: '13px', lg: '12px', md: '11px' }, backgroundColor: '#DDEEFD', p: 0.5, borderRadius: 2 }} justifySelf={"right"}>
                                                        Indications
                                                    </Typography>
                                                </Grid>

                                                <Grid size={{ xs: 12, md: 10 }}  >
                                                    <Grid container >
                                                        <Grid sx={{ display: 'flex', padding: '0.5rem', flexWrap: 'wrap' }}>
                                                            {config.CesareanSectionIndications?.map((item, id) => (
                                                                <Typography
                                                                    sx={{
                                                                        textAlign: 'center',
                                                                        fontSize: {xl: '14px', lg: '12px', md: '11px'},
                                                                        cursor: 'pointer',
                                                                        border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]["csIndications"]?.find((indication) => indication.label === item.label) ? '1px solid #919191' : 'none',
                                                                        p: 0.5,
                                                                        m: 0.5,
                                                                        borderRadius: 2,
                                                                        fontSize: { xl: '14px', lg: '12px', md: '11px' },
                                                                    }}
                                                                    alignSelf="center"
                                                                    alignItems="center"
                                                                    index={id}
                                                                    onClick={() => {
                                                                        changeSingleChoiceOption(code, "obstetricHistories", index, "csIndications", item)
                                                                    }}
                                                                >
                                                                    {item.label}
                                                                </Typography>
                                                            ))}
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </>
                                        }
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 12 }} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)', p: 1, borderRadius: 5, }}>
                                        <Grid size={{ xs: 12, md: 12 }} mt={0.5}  >
                                            <Typography variant='body1' textAlign={'left'} sx={headerLabel} >Child</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 12 }}   >
                                            <Grid container>
                                                <Grid size={{ xs: 12, md: 5 }}>
                                                    <Grid container sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} mt={1}>
                                                        <Typography variant='body1' sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px', textAlign: 'center' } }} >Gender</Typography>
                                                        {config?.genderOfChildTypes?.map((item) => {
                                                            return <Grid size={{ xs: 4, md: 4 }} >
                                                                <Typography sx={{ cursor: 'pointer', fontSize: { xl: '14px', lg: '12px', md: '11px' }, border: historyBlocks?.[code]?.data?.["obstetricHistories"]?.[index]["genderOfChild"] == item.value ? '1px solid #919191' : 'none', justifySelf: 'flex-end', borderRadius: 2, p: 0.5, width: '70%' }}
                                                                    onClick={() => {
                                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "genderOfChild", item.value)

                                                                    }}
                                                                // fontSize={"14px"}
                                                                >
                                                                    {item.label}
                                                                </Typography>
                                                            </Grid>
                                                        })}
                                                    </Grid>
                                                </Grid>

                                                <Grid size={{ xs: 12, md: 7 }}>
                                                    <Grid container sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} mt={1} spacing={1}>
                                                        <Typography variant='body1' sx={headerLabel} >Birth Weight</Typography>
                                                        <TextField
                                                            style={{
                                                                width: '15%'
                                                            }}
                                                            value={historyBlocks?.[code]?.data?.obstetricHistories[index].birthWeightKg ?? ""}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
                                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "birthWeightKg", e.target.value == "" ? null : parseInt(e.target.value))
                                                                }
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    '& fieldset': {
                                                                        border: '1px solid #919191',
                                                                    },
                                                                    '& input': {
                                                                        fontSize: '12px',
                                                                        height: '8px',
                                                                        padding: '10px',
                                                                        backgroundColor: '#FFFF',
                                                                        borderRadius: 2
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                        <Typography textAlign={"center"} fontSize={"14px"} sx={{ fontSize: { xl: '14px', lg: '12px', md: '11px' } }}>kg</Typography>

                                                        <TextField
                                                            style={{
                                                                width: '15%'
                                                            }}
                                                            value={historyBlocks?.[code]?.data?.obstetricHistories[index].birthWeightGm ?? ""}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    '& fieldset': {
                                                                        border: '1px solid #919191',
                                                                    },
                                                                    '& input': {
                                                                        fontSize: '12px',
                                                                        height: '8px',
                                                                        padding: '10px',
                                                                        backgroundColor: '#FFFF',
                                                                        borderRadius: 2
                                                                    },
                                                                },
                                                            }}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
                                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "birthWeightGm", value == "" ? null : parseInt(e.target.value))
                                                                }
                                                            }}
                                                        />
                                                        <Typography textAlign={"center"} fontSize={"14px"} sx={{ fontSize: { xl: '14px', lg: '12px', md: '11px' } }}>g</Typography>

                                                    </Grid>
                                                </Grid>

                                                <Grid size={{ xs: 12, md: 12 }} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0.5, md: 0 } }} mt={1}>
                                                    <Grid size={{ xs: 12, md: 2, }}>
                                                        <Typography textAlign={'left'} fontSize={"12px"} sx={headerLabel}>Neonatal Outcome</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 10 }}>
                                                        <Grid container sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>

                                                            {
                                                                config?.neonatalOutcomeTypes?.map((item, itemIndex) => {
                                                                    return <Grid sx={{ ml: itemIndex > 0 ? 2 : 1 }} >
                                                                        <Typography sx={{
                                                                            cursor: 'pointer',
                                                                            border: item.label == historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.neonatalOutcome?.label ? '1px solid #919191' : 'none',
                                                                            borderRadius: 2, p: 0.5,
                                                                            fontSize: {xl: '14px', lg: '12px', md: '11px'}
                                                                        }}
                                                                            // fontSize={"12px"}
                                                                            onClick={() => handleChangeObstetricsParentChildren(code, "obstetricHistories", index, "neonatalOutcome", "label", item.value, item.label)
                                                                            }
                                                                        >
                                                                            {item.label}
                                                                        </Typography>
                                                                    </Grid>
                                                                })
                                                            }
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 12 }} mt={0.3}>

                                                    <Grid container>
                                                        <Grid size={{ xs: 12, md: getLeftSpace(historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.neonatalOutcome?.value) }}></Grid>
                                                        {selectedItem && selectedItem.children.length > 0 && <Box sx={{ display: { xs: 'none', md: 'inline-block' }, lineHeight: 0 }}><svg xmlns="http://www.w3.org/2000/svg"
                                                            width="60"
                                                            height="25"
                                                            viewBox="0 0 150 200"
                                                        >
                                                            <path
                                                                d="M10 100 Q 180 0, 190 180"
                                                                stroke="black"
                                                                strokeWidth="5"
                                                                fill="transparent"
                                                                transform="rotate(200 100 100)"
                                                            />
                                                        </svg></Box>}

                                                        {config?.neonatalOutcomeTypes?.map(item => {
                                                            return item.value == historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.neonatalOutcome?.value && item.children.map((innerchild, innerIndex) => (
                                                                <Grid sx={{ ml: innerIndex > 0 ? 2 : 0 }} >
                                                                    <Typography fontSize="14px"
                                                                        sx={{
                                                                            cursor: 'pointer',
                                                                            border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.neonatalOutcome?.children.find((it) => it.label == innerchild.label) ? '1px solid #919191' : 'none',
                                                                            justifySelf: 'flex-start',
                                                                            borderRadius: 2,
                                                                            p: 0.5,
                                                                            fontSize: { xl: '14px', lg: '12px', md: '11px' }
                                                                        }}
                                                                        onClick={() => {
                                                                            const findingChild = item.children.find((child) => child.value == innerchild.value)
                                                                            const children = [{ ...findingChild, }]
                                                                            handleChangeObstetricsParentChildren(code, "obstetricHistories", index, "neonatalOutcome", "children", children)
                                                                        }}
                                                                    >
                                                                        {innerchild.label}
                                                                    </Typography>


                                                                </Grid>
                                                            ))
                                                        })}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 12 }} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)', p: 1, borderRadius: 5, }}>
                                        <Grid container mt={1}>
                                            <Grid size={{ xs: 12, md: 2.5 }}   >
                                                <Typography variant='body1' textAlign={'left'} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }} >Operative Procedure</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 9.5 }}  >
                                                <Grid sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                                    {config?.operativeProcedures?.map((item, id) => (
                                                        <Typography
                                                            sx={{
                                                                textAlign: 'center',
                                                                // fontSize: '14px',
                                                                cursor: 'pointer',
                                                                border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.["operativeProcedure"]?.find((indication) => indication.label === item.label) ? '1px solid #919191' : 'none',
                                                                p: 0.5,
                                                                m: 0.5,
                                                                borderRadius: 2,
                                                                fontSize: { xl: '14px', lg: '12px', md: '11px' }
                                                            }}
                                                            alignSelf="center"
                                                            alignItems="center"
                                                            index={id}
                                                            onClick={() => {
                                                                changeSingleChoiceOption(code, "obstetricHistories", index, "operativeProcedure", item)

                                                            }}
                                                        >
                                                            {item.label}
                                                        </Typography>
                                                    ))}
                                                </Grid>
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 12 }} mt={1} >
                                                <Grid container>
                                                    <Grid size={{ xs: 12, md: 4 }}   >
                                                        <Typography variant='body1' textAlign={'left'} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }}>Complication in procedure</Typography>
                                                        <Grid sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>
                                                            <Typography sx={{ width: '45%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasComplicationProcedure ? '1px solid #919191' : 'none', borderRadius: 2,p:0.4 }}
                                                                onClick={() => {
                                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "hasComplicationProcedure", true)
                                                                }}>
                                                                Yes
                                                            </Typography>
                                                            <Typography sx={{ width: '45%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasComplicationProcedure == false ? '1px solid #919191' : 'none', borderRadius: 2,p:0.4 }} onClick={() => {
                                                                handleChangeObstetricsData(code, "obstetricHistories", index, "hasComplicationProcedure", false)
                                                            }}>
                                                                No
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 4 }}   >
                                                        <Typography variant='body1' textAlign={'left'} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }}>Blood Transfusion</Typography>

                                                        <Grid sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>
                                                            <Typography sx={{ width: '45%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasBloodTransfusion ? '1px solid #919191' : 'none', borderRadius: 2,p:0.4 }}
                                                                onClick={() => {
                                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "hasBloodTransfusion", true)
                                                                }}>
                                                                Yes
                                                            </Typography>
                                                            <Typography sx={{ width: '45%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasBloodTransfusion == false ? '1px solid #919191' : 'none', borderRadius: 2,p:0.4}} onClick={() => {
                                                                handleChangeObstetricsData(code, "obstetricHistories", index, "hasBloodTransfusion", false)
                                                            }}>
                                                                No
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 4 }}   >
                                                        <Typography variant='body1' textAlign={'left'} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }}>Given Anesthesia</Typography>
                                                        <Grid sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>
                                                            <Typography sx={{ width: '40%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasUsedAnesthesia ? '1px solid #919191' : 'none', borderRadius: 2,p:0.4 }}
                                                                onClick={() => {
                                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "hasUsedAnesthesia", true)
                                                                }}>
                                                                Yes
                                                            </Typography>
                                                            <Typography sx={{ width: '40%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasUsedAnesthesia == false ? '1px solid #919191' : 'none', borderRadius: 2,p:0.4 }} onClick={() => {
                                                                handleChangeObstetricsData(code, "obstetricHistories", index, "hasUsedAnesthesia", false)
                                                                handleChangeObstetricsData(code, "obstetricHistories", index, "anesthesiaTypes", [])
                                                            }}>
                                                                No
                                                            </Typography>
                                                        </Grid>

                                                        {historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasUsedAnesthesia && <Grid container sx={{}}  >
                                                            <Grid sx={{ display: 'flex', flexDirection: 'row', }} size={{ xs: 1, md: 2 }}>
                                                                <Box sx={{ display: { xs: 'none', md: 'inline-block' }, lineHeight: 0 }}><svg xmlns="http://www.w3.org/2000/svg"
                                                                    width="60"
                                                                    height="40"
                                                                    viewBox="0 0 150 200"
                                                                >
                                                                    <path
                                                                        d="M10 100 Q 180 0, 190 180"
                                                                        stroke="black"
                                                                        strokeWidth="5"
                                                                        fill="transparent"
                                                                        transform="rotate(200 100 100)"
                                                                    />
                                                                </svg></Box>
                                                            </Grid>

                                                            <Grid sx={{ display: 'flex', flexWrap: 'wrap', }} mt={1} size={{ xs: 1, md: 10 }} justifySelf={"flex-end"} >
                                                                {config?.anesthesiaTypes?.map((item, id) => (
                                                                    <Typography
                                                                        sx={{
                                                                            textAlign: 'center',
                                                                            // fontSize: '14px',
                                                                            cursor: 'pointer',
                                                                            border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.["anesthesiaTypes"]?.find((indication) => indication.label === item.label) ? '1px solid #919191' : 'none',
                                                                            p: 0.5,
                                                                            m: 0.5,
                                                                            borderRadius: 2,
                                                                            fontSize: { xl: '14px', lg: '12px', md: '11px' }
                                                                        }}
                                                                        alignSelf="center"
                                                                        alignItems="center"
                                                                        index={id}
                                                                        onClick={() => {
                                                                            changeSingleChoiceOption(code, "obstetricHistories", index, "anesthesiaTypes", item)
                                                                        }}
                                                                    >
                                                                        {item.label}
                                                                    </Typography>
                                                                ))}
                                                            </Grid>
                                                        </Grid>}
                                                    </Grid>
                                                </Grid>
                                            </Grid>


                                        </Grid>

                                    </Grid>

                                    <Grid size={{ xs: 12, md: 12 }} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)', p: 1, borderRadius: 5, }}>
                                        <Grid container mt={1}>
                                            <Grid size={{ xs: 12, md: 4 }}   >
                                                <Typography variant='body1' textAlign={'left'} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }} >Intra & Postpartum complication </Typography>
                                            </Grid>

                                            <Grid sx={{ display: 'flex', flexDirection: 'row', }} size={{ xs: 12, md: 8 }} justifyContent={"flex-end"} >
                                                <Typography sx={{ width: '25%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasAncComplications ? '1px solid #919191' : 'none', borderRadius: 2, display: 'flex', height: '18px', justifyContent: 'center', alignItems: 'center',p:0.5 }}
                                                    onClick={() => {
                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "hasAncComplications", true)


                                                    }}>
                                                    Yes
                                                </Typography>
                                                <Typography sx={{ width: '25%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasAncComplications == false ? '1px solid #919191' : 'none', borderRadius: 2, display: 'flex', height: '18px', justifyContent: 'center', alignItems: 'center',p:0.5 }} onClick={() => {
                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "hasAncComplications", false)
                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "ancComplications", [])

                                                }}>
                                                    No
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 1 }} >
                                            </Grid>

                                            {
                                                historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasAncComplications && <>
                                                    <Grid size={{ xs: 12, md: 8 }} mt={1} sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }} justifySelf={"flex-end"} justifyContent={"flex-end"}>
                                                        {config?.intraPostpartumComplications?.map((item, id) => (
                                                            <Typography
                                                                sx={{
                                                                    // fontSize: '14px',
                                                                    cursor: 'pointer',
                                                                    border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.["ancComplications"]?.find((indication) => indication.label === item.label) ? '1px solid #919191' : 'none',
                                                                    p: 0.5,
                                                                    m: 0.5,
                                                                    borderRadius: 2,
                                                                    fontSize: { xl: '14px', lg: '12px', md: '11px' }
                                                                }}
                                                                index={id}
                                                                onClick={() => {
                                                                    changeSingleChoiceOption(code, "obstetricHistories", index, "ancComplications", item)
                                                                }}
                                                            >
                                                                {item.label}
                                                            </Typography>
                                                        ))}
                                                    </Grid>

                                                    <Box sx={{ display: { xs: 'none', md: 'inline-block' }, lineHeight: 0 }}><svg xmlns="http://www.w3.org/2000/svg"
                                                        width="30"
                                                        height="40"
                                                        viewBox="0 0 150 200"

                                                    >
                                                        <path
                                                            d="M10 100 Q 180 0, 190 180"
                                                            stroke="black"
                                                            strokeWidth="5"
                                                            fill="transparent"
                                                            transform="rotate(105 100 100)"
                                                        />
                                                    </svg></Box>
                                                </>
                                            }
                                        </Grid>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 12 }} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)', p: 1, borderRadius: 5, }}>
                                        <Grid container mt={1}>
                                            <Grid size={{ xs: 12, md: 4 }}   >
                                                <Typography variant='body1' textAlign={'left'} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }} >During and After Delivery Complications </Typography>
                                            </Grid>

                                            <Grid sx={{ display: 'flex', flexDirection: 'row', }} size={{ xs: 12, md: 8 }} justifyContent={"flex-end"} >
                                                <Typography sx={{ width: '25%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasDuringAfterDeliveryComplications ? '1px solid #919191' : 'none', borderRadius: 2, display: 'flex', height: '18px', justifyContent: 'center', alignItems: 'center',p:0.5 }}
                                                    onClick={() => {
                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "hasDuringAfterDeliveryComplications", true)


                                                    }}>
                                                    Yes
                                                </Typography>
                                                <Typography sx={{ width: '25%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasDuringAfterDeliveryComplications == false ? '1px solid #919191' : 'none', borderRadius: 2, display: 'flex', height: '18px', justifyContent: 'center', alignItems: 'center',p:0.5 }} onClick={() => {
                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "hasDuringAfterDeliveryComplications", false)
                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "duringAfterDeliveryComplications", [])

                                                }}>
                                                    No
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 2 }} >
                                            </Grid>

                                            {
                                                historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasDuringAfterDeliveryComplications && <>
                                                    <Grid size={{ xs: 12, md: 7 }} mt={1}>
                                                        <Grid sx={{ display: 'flex', flexWrap: 'wrap', }} >
                                                            {config?.duringAndAfterDeliveryComplications?.map((item, id) => (
                                                                <Typography
                                                                    sx={{
                                                                        // fontSize: '14px',
                                                                        cursor: 'pointer',
                                                                        border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.["duringAfterDeliveryComplications"]?.find((indication) => indication.label === item.label) ? '1px solid #919191' : 'none',
                                                                        p: 0.5,
                                                                        m: 0.5,
                                                                        borderRadius: 2,
                                                                        fontSize: { xl: '14px', lg: '12px', md: '11px' }
                                                                    }}
                                                                    index={id}
                                                                    onClick={() => {
                                                                        changeSingleChoiceOption(code, "obstetricHistories", index, "duringAfterDeliveryComplications", item)
                                                                    }}
                                                                >
                                                                    {item.label}
                                                                </Typography>
                                                            ))}
                                                        </Grid>
                                                    </Grid>

                                                    <Box sx={{ display: { xs: 'none', md: 'inline-block' }, lineHeight: 0 }}><svg xmlns="http://www.w3.org/2000/svg"
                                                        width="30"
                                                        height="40"
                                                        viewBox="0 0 150 200"

                                                    >
                                                        <path
                                                            d="M10 100 Q 180 0, 190 180"
                                                            stroke="black"
                                                            strokeWidth="5"
                                                            fill="transparent"
                                                            transform="rotate(105 100 100)"
                                                        />
                                                    </svg></Box>
                                                </>
                                            }
                                        </Grid>
                                    </Grid>

                                </Grid>
                            </Grid>
                        </Grid>
                            :
                            <ObstetricHistoryCard savedPastHistory={historyBlocks?.[code]?.data} index={index} label="Pre Term Birth" />
                        : item.historyType === "ABORTION" ?

                            <Grid container sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)', p: 1, mt: 1, borderRadius: 5, }} >
                                {/* <Box sx={{
                        position: 'absolute',
                        right: 50,
                        height: "800px",
                        width: 5,
                        bgcolor: 'green',
                        transition: "background-color 0.3s ease"
                    }} /> */}
                                <Grid size={{ xs: 12, md: 10, }} mt={1} >
                                    <Typography textAlign={'left'} fontSize={"17px"} sx={{ fontSize: { xl: '17px', lg: '16px', md: '13px' } }}>
                                        Abortion
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 2, }} mt={1} >
                                    <Typography fontSize={"18px"} sx={{ fontSize: { xl: '18px', lg: '16px', md: '13px' } }}>
                                        {historyBlocks?.[code]?.data?.obstetricHistories?.[index].date ? moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index].date).format('MMM YYYY') : ''}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 12, }}  >
                                    <Grid container>
                                        {/* Week Of Abortion */}
                                        <Grid size={{ xs: 12, md: 6, }} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>
                                            <Grid size={{ xs: 12, md: 5, }}  >
                                                <Typography textAlign={'left'} fontSize={"12px"} sx={headerLabel}>
                                                    Week Of Abortion
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 0, md: 1, }} sx={{ display: { xs: 'none', md: 'block' } }} />
                                            <Grid size={{ xs: 12, md: 6, }} >
                                                <Box sx={{ backgroundColor: '#FFF', borderRadius: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', width: { xs: '120px', md: '48%' }, px: 1 }}>
                                                    <CustomSelectBox
                                                        value={item.week || ''}
                                                        handleChange={(e) => {
                                                            handleChangeObstetricsData(code, "obstetricHistories", index, "week", parseInt(e))
                                                        }}
                                                        placeholder="00"
                                                        options={abortionWeeks}
                                                        width={'50px'}
                                                        selectStyle={{
                                                            height: '25px',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }}
                                                        edit={edit}
                                                    />
                                                    <Typography textAlign={'center'} alignSelf={'center'} fontSize={"12px"} sx={{ fontSize: { xl: '12px', lg: '11px', md: '8px' }, ml:0.5 }}>Wk</Typography>
                                                </Box>

                                            </Grid>
                                        </Grid>

                                        {/*  place of delivery */}

                                        <Grid size={{ xs: 12, md: 6, }} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>
                                            <Grid size={{ xs: 12, md: 5.5, }} >
                                                <Typography textAlign={'left'} fontSize={"12px"} sx={headerLabel}>
                                                    Place Of Abortion
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 0, md: 0, }} sx={{ display: { xs: 'none', md: 'block' } }}>
                                            </Grid>

                                            <Grid size={{ xs: 6, md: 6.5, }} >
                                                <Box sx={{ backgroundColor: '#FFF', borderRadius: 2, display: 'flex', flexDirection: 'row', width: { xl: '50%', lg: '70%', md: '100%' } }}>
                                                    <Autocomplete
                                                        options={constantReducer.cities}
                                                        value={item.place || null}
                                                        onChange={(event, newValue) => {
                                                            handleChangeObstetricsData(code, "obstetricHistories", index, "place", newValue);
                                                        }}
                                                        size="small"
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                // sx={{
                                                                //     '& .MuiOutlinedInput-root': {
                                                                //         '& fieldset': {
                                                                //             border: '1px solid #919191',
                                                                //         },
                                                                //         padding: '0px',
                                                                //         '& input': {
                                                                //             padding: '6px 8px',
                                                                //             fontSize: '0.75rem',
                                                                //             lineHeight: 1,
                                                                //         },
                                                                //     },
                                                                //     width: '100%',
                                                                // }}
                                                            />
                                                        )}
                                                        sx={{
                                                            width: '100%',
                                                            '& .MuiAutocomplete-inputRoot': {
                                                                padding: '0px !important',
                                                                fontSize: '0.75rem',
                                                            },
                                                            '& .MuiAutocomplete-option': {
                                                                fontSize: '0.75rem',
                                                                padding: '4px 8px',
                                                            },
                                                        }}
                                                        disableClearable
                                                        disabled={!edit}
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 12 }} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0.5, md: 0 } }} mt={1}>
                                            <Grid size={{ xs: 12, md: 2 }}>
                                                <Typography textAlign={"left"} fontSize={"12px"} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }}>Abortion Type</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 10 }} sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                                                {
                                                    edit ? config?.abortionTypes?.map(item => {
                                                        return <Typography sx={{
                                                            cursor: edit ? 'pointer' : 'default',
                                                            border: item.label == historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.type?.label ? '1px solid #919191' : 'none',
                                                            borderRadius: 2, p: 0.5,
                                                            fontSize: { xl: '14px', lg: '12px', md: '11px' },
                                                            marginLeft: '3px'
                                                        }}
                                                            onClick={() => edit && handleChangeObstetricsParentChildren(code, "obstetricHistories", index, "type", "label", item.value, item.label)
                                                            }
                                                        >
                                                            {item.label}
                                                        </Typography>
                                                    })
                                                        :
                                                        <Grid size={{ xs: 12, xl: 3, lg: 4, md: 4 }} >
                                                            <Typography sx={{
                                                                cursor: 'default',
                                                                border: '1px solid #919191',
                                                                borderRadius: 2, p: 0.5,
                                                                fontSize: {  xl: '14px', lg: '12px', md: '11px' }
                                                            }}
                                                            >
                                                                {historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.type?.label || '--'}
                                                            </Typography>
                                                        </Grid>
                                                }
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            :
                            item.historyType === "TERM" &&
                                edit ? <Grid container sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)', p: 1, mt: 1, borderRadius: 5 }} >
                                <Grid size={{ xs: 12, md: 10, }} mt={1} >
                                    <Typography textAlign={'left'} fontSize={"17px"} sx={{ fontSize: { xl: '15px', lg: '13px', md: '11px' } }}>
                                        Term Birth
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 2, }} mt={1} >
                                    <Typography fontSize={"18px"} sx={{ fontSize: { xl: '18px', lg: '16px', md: '13px' } }}>
                                        {historyBlocks?.[code]?.data?.obstetricHistories?.[index].date ? moment(historyBlocks?.[code]?.data?.obstetricHistories?.[index].date).format('MMM YYYY') : ''}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 12, }}  >
                                    <Grid container>
                                        {/* Week Of Delivery */}
                                        <Grid size={{ xs: 12, md: 6, }} sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>
                                            <Grid size={{ xs: 6, md: 5, }}  >
                                                <Typography textAlign={'left'} fontSize={"12px"} sx={headerLabel}>
                                                    Week Of Delivery
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 0, md: 1, }} sx={{ display: { xs: 'none', md: 'block' } }} />
                                            <Grid size={{ xs: 6, md: 6, }} >
                                                <Box sx={{ backgroundColor: '#FFF', borderRadius: 4, display: 'flex', flexDirection: 'row', alignItems: 'center', width: { xs: '120px', md: '48%' }, px: 1 }}>
                                                    <CustomSelectBox
                                                        value={item.week || ''}
                                                        handleChange={(e) => {
                                                            handleChangeObstetricsData(code, "obstetricHistories", index, "week", parseInt(e))
                                                        }}
                                                        placeholder="00"
                                                        options={termWeeks}
                                                        width={'60px'}
                                                        selectStyle={{
                                                            height: '25px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            px:'3px'
                                                        }}
                                                        edit={edit}
                                                    />
                                                    <Typography textAlign={'center'} alignSelf={'center'} fontSize={"12px"} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' }, ml:0.5 }}>Wk</Typography>
                                                </Box>

                                            </Grid>
                                        </Grid>

                                        {/*  place of delivery */}

                                        <Grid size={{ xs: 12, md: 6, }} sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'row' }, gap: { xs: 0.5, md: 0 } }}  mt={1}>
                                            <Grid size={{ xs: 6, md: 5.5, }} >
                                                <Typography textAlign={'left'} fontSize={"12px"} sx={headerLabel}>
                                                    Place Of Delivery
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 0, md: 0, }} sx={{ display: { xs: 'none', md: 'block' } }}>
                                            </Grid>

                                            <Grid size={{ xs: 6, md: 6.5, }} >
                                                <Box sx={{ backgroundColor: '#FFF', borderRadius: 4, display: 'flex', flexDirection: 'row', width: { xl: '50%', lg: '70%', md: '100%' } }}>

                                                    <Autocomplete
                                                        options={constantReducer.cities}
                                                        value={item.place || null}
                                                        onChange={(event, newValue) => {
                                                            handleChangeObstetricsData(code, "obstetricHistories", index, "place", newValue);
                                                        }}
                                                        size="small"
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': {
                                                                            border: '1px solid #919191'
                                                                        },
                                                                        padding: '0px',
                                                                        '& input': {
                                                                            padding: '6px 8px',
                                                                            fontSize: '0.75rem',
                                                                            lineHeight: 1,
                                                                        },
                                                                    },
                                                                    width: '100%',
                                                                }}
                                                            />
                                                        )}
                                                        sx={{
                                                            width: '100%',
                                                            '& .MuiAutocomplete-inputRoot': {
                                                                padding: '0px !important',
                                                                fontSize: '0.75rem',
                                                            },
                                                            '& .MuiAutocomplete-option': {
                                                                fontSize: '0.75rem',
                                                                padding: '4px 8px',
                                                            },
                                                        }}
                                                        disableClearable
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 12 }} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0.5, md: 0 } }} mt={1}>
                                            <Grid size={{ xs: 12, md: 2 }}>
                                                <Typography textAlign={"left"} fontSize={"12px"} sx={headerLabel}>Delivery Type</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 10 }}>
                                                <Grid container sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: { xs: 0.5, md: 0 } }}>
                                                    <Grid size={{ xs: 0, md: 2 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                                                    </Grid>
                                                    {
                                                        config?.termDeliveryTypes?.map(item => {
                                                            return <Grid size={{ xs: 'auto', md: 3 }} >
                                                                <Typography sx={{
                                                                    cursor: 'pointer',
                                                                    borderRadius: 2, p: 0.5,
                                                                    fontSize: {  xl: '14px', lg: '12px', md: '11px' },
                                                                    border: item.label == historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.type?.label ? '1px solid #919191' : 'none'
                                                                }}
                                                                    // fontSize={"12px"}
                                                                    onClick={() => handleChangeObstetricsParentChildren(code, "obstetricHistories", index, "type", "label", item.value, item.label)
                                                                    }
                                                                >
                                                                    {item.label}
                                                                </Typography>
                                                            </Grid>
                                                        })
                                                    }
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 12 }} mt={1}>
                                            <Grid container sx={{ flexWrap: 'wrap', gap: { xs: 0.5, md: 0 } }}>
                                                {config?.termDeliveryTypes?.map(item => {
                                                    return item.value == historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.type?.value && item.children.map((child) => (
                                                        <Grid size={{ xs: 'auto', md: 2 }} >
                                                            <Typography fontSize="14px"
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    justifySelf: item.value == "CESAREAN_SECTION" ? "flex-end" : 'flex-start',
                                                                    borderRadius: 2,
                                                                    p: 0.5,
                                                                    fontSize: {  xl: '14px', lg: '12px', md: '11px' },
                                                                    border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.type?.children.find((it) => it.children.find((und) => und.label == child.label)) ? '1px solid #919191' : 'none'
                                                                }}
                                                                onClick={() => {
                                                                    const findingChild = item.children.find((child) => child.value == "TERM")
                                                                    const children = [{ ...findingChild, ["children"]: [child] }]
                                                                    handleChangeObstetricsParentChildren(code, "obstetricHistories", index, "type", "children", children)
                                                                }}
                                                            >
                                                                {child.label}
                                                            </Typography>


                                                        </Grid>
                                                    ))
                                                })}

                                                {historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.type?.value && <Box sx={{ display: { xs: 'none', md: 'inline-block' }, lineHeight: 0 }}><svg xmlns="http://www.w3.org/2000/svg"
                                                    width="40"
                                                    height="50"
                                                    viewBox="80 50 140 250"
                                                >
                                                    <path
                                                        d="M10 100 Q 100 0, 190 140"
                                                        stroke="black"
                                                        strokeWidth="5"
                                                        fill="transparent"
                                                        transform="rotate(135 100 100)"
                                                    />
                                                </svg></Box>}


                                            </Grid>

                                        </Grid>
                                        <Grid size={{ xs: 12, md: 12 }} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>
                                            {historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.type?.value == "CESAREAN_SECTION" &&
                                                <>
                                                    <Grid size={{ xs: 12, md: 2 }} mt={1}>
                                                        <Typography fontSize={"13px"} sx={{ fontSize: { xl: '13px', lg: '12px', md: '11px' }, backgroundColor: '#DDEEFD', p: 0.5, borderRadius: 2 }} justifySelf={'right'}>
                                                            Indications
                                                        </Typography>
                                                    </Grid>

                                                    <Grid size={{ xs: 12, md: 10 }}  >
                                                        <Grid sx={{ display: 'flex', padding: '0.5rem', flexWrap: 'wrap' }}>
                                                            {config?.CesareanSectionIndications?.map((item, id) => (
                                                                <Typography
                                                                    sx={{
                                                                        textAlign: 'center',
                                                                        fontSize: { xl: '14px', lg: '12px', md: '11px'},
                                                                        cursor: 'pointer',
                                                                        p: 0.5,
                                                                        m: 0.5,
                                                                        borderRadius: 2,
                                                                        border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]["csIndications"]?.find((indication) => indication.label === item.label) ? '1px solid #919191' : 'none'
                                                                    }}
                                                                    alignSelf="center"
                                                                    alignItems="center"
                                                                    index={id}
                                                                    onClick={() => {
                                                                        changeSingleChoiceOption(code, "obstetricHistories", index, "csIndications", item)
                                                                    }}
                                                                >
                                                                    {item.label}
                                                                </Typography>
                                                            ))}
                                                        </Grid>
                                                    </Grid>
                                                </>
                                            }

                                        </Grid>

                                        <Grid size={{ xs: 12, md: 12 }} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)', p: 1, borderRadius: 5, }}>
                                            <Grid size={{ xs: 12, md: 12 }} mt={0.5}  >
                                                <Typography variant='body1' textAlign={'left'} sx={headerLabel} >Child</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 12 }}  >
                                                <Grid container>
                                                    <Grid size={{ xs: 12, md: 5 }}>

                                                        <Grid container sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} >
                                                            <Typography variant='body1' sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px', textAlign: 'center', } }} >Gender</Typography>
                                                            {config?.genderOfChildTypes?.map((item) => {
                                                                return <Grid size={{xs: 4, md: 4 }} >
                                                                    <Typography sx={{ fontSize: {  xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.["obstetricHistories"]?.[index]["genderOfChild"] == item.value ? '1px solid #919191' : 'none', justifySelf: 'flex-end', borderRadius: 2, p: 0.5, width: '70%' }}
                                                                        onClick={() => {
                                                                            handleChangeObstetricsData(code, "obstetricHistories", index, "genderOfChild", item.value)

                                                                        }}
                                                                    >
                                                                        {item.label}
                                                                    </Typography>
                                                                </Grid>
                                                            })}
                                                        </Grid>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 7 }}>

                                                        <Grid container sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} spacing={1}>
                                                            <Typography variant='body1' sx={headerLabel} >Birth Weight</Typography>
                                                            <TextField
                                                                style={{
                                                                    width: '15%'
                                                                }}
                                                                value={historyBlocks?.[code]?.data?.obstetricHistories[index].birthWeightKg ?? ""}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
                                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "birthWeightKg", e.target.value == "" ? null : parseInt(e.target.value))
                                                                    }
                                                                }}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': {
                                                                            border: '1px solid #919191',
                                                                        },
                                                                        '& input': {
                                                                            fontSize: '12px',
                                                                            height: '8px',
                                                                            padding: '10px',
                                                                            backgroundColor: '#FFFF',
                                                                            borderRadius: 2
                                                                        },
                                                                    },
                                                                }}
                                                            />
                                                            <Typography textAlign={"center"} fontSize={"14px"} sx={{ fontSize: { xl: '14px', lg: '12px', md: '11px' } }}>kg</Typography>

                                                            <TextField
                                                                style={{
                                                                    width: '15%'
                                                                }}
                                                                value={historyBlocks?.[code]?.data?.obstetricHistories[index].birthWeightGm ?? ""}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': {
                                                                            border: '1px solid #919191',
                                                                        },
                                                                        '& input': {
                                                                            fontSize: '12px',
                                                                            height: '8px',
                                                                            padding: '10px',
                                                                            backgroundColor: '#FFFF',
                                                                            borderRadius: 2
                                                                        },
                                                                    },
                                                                }}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
                                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "birthWeightGm", value == "" ? null : parseInt(e.target.value))
                                                                    }
                                                                }}
                                                            />
                                                            <Typography textAlign={"center"} fontSize={"14px"} sx={{ fontSize: { xl: '14px', lg: '12px', md: '11px' } }}>g</Typography>

                                                        </Grid>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 12 }} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0.5, md: 0 } }} mt={1}>
                                                        <Grid size={{ xs: 12, md: 2, }}>
                                                            <Typography textAlign={"left"} fontSize={"12px"} sx={headerLabel}>Neonatal Outcome</Typography>
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 10 }}>
                                                            <Grid container sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>

                                                                {
                                                                    config?.neonatalOutcomeTypes?.map((item, itemIndex) => {
                                                                        return <Grid sx={{ ml: itemIndex > 0 ? 2 : 1 }} >
                                                                            <Typography sx={{
                                                                                cursor: 'pointer',
                                                                                border: item.label == historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.neonatalOutcome?.label ? '1px solid #919191' : 'none',
                                                                                borderRadius: 2, p: 0.5,
                                                                                fontSize: { xl: '14px', lg: '12px', md: '11px' }
                                                                            }}
                                                                                // fontSize={"12px"}
                                                                                onClick={() => handleChangeObstetricsParentChildren(code, "obstetricHistories", index, "neonatalOutcome", "label", item.value, item.label)
                                                                                }
                                                                            >
                                                                                {item.label}
                                                                            </Typography>
                                                                        </Grid>
                                                                    })
                                                                }
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 12 }} mt={0.3}>

                                                        <Grid container>
                                                            <Grid size={{ xs: 12, md: getLeftSpace(historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.neonatalOutcome?.value) }}></Grid>
                                                            {selectedItem && selectedItem.children.length > 0 && <Box sx={{ display: { xs: 'none', md: 'inline-block' }, lineHeight: 0 }}><svg xmlns="http://www.w3.org/2000/svg"
                                                                width="60"
                                                                height="25"
                                                                viewBox="0 0 150 200"
                                                            >
                                                                <path
                                                                    d="M10 100 Q 180 0, 190 180"
                                                                    stroke="black"
                                                                    strokeWidth="5"
                                                                    fill="transparent"
                                                                    transform="rotate(200 100 100)"
                                                                />
                                                            </svg></Box>}

                                                            {config?.neonatalOutcomeTypes?.map(item => {
                                                                return item.value == historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.neonatalOutcome?.value && item.children.map((innerchild, innerIndex) => (
                                                                    <Grid sx={{ ml: innerIndex > 0 ? 2 : 0 }}  >
                                                                        <Typography fontSize="14px"
                                                                            sx={{
                                                                                cursor: 'pointer',
                                                                                border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.neonatalOutcome?.children.find((it) => it.children.find((und) => und.label == innerchild.label)) ? '1px solid #919191' : 'none',
                                                                                justifySelf: 'flex-start',
                                                                                borderRadius: 2,
                                                                                p: 0.5,
                                                                                fontSize: { xl: '14px', lg: '12px', md: '11px' }
                                                                            }}
                                                                            onClick={() => {
                                                                                const findingChild = item.children.find((child) => child.value == innerchild.value)
                                                                                const children = [{ ...findingChild, ["children"]: [innerchild] }]
                                                                                handleChangeObstetricsParentChildren(code, "obstetricHistories", index, "neonatalOutcome", "children", children)
                                                                            }}
                                                                        >
                                                                            {innerchild.label}
                                                                        </Typography>


                                                                    </Grid>
                                                                ))
                                                            })}
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 12 }} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)', p: 1, borderRadius: 5, }}>
                                            <Grid container mt={1}>
                                                <Grid size={{ xs: 12, md: 2.5 }}   >
                                                    <Typography variant='body1' textAlign={'left'} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }} >Operative Procedure</Typography>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 9.5 }}  >
                                                    <Grid sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                                        {config?.operativeProcedures?.map((item, id) => (
                                                            <Typography
                                                                sx={{
                                                                    textAlign: 'center',
                                                                    // fontSize: '14px',
                                                                    cursor: 'pointer',
                                                                    border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.["operativeProcedure"]?.find((indication) => indication.label === item.label) ? '1px solid #919191' : 'none',
                                                                    p: 0.5,
                                                                    m: 0.5,
                                                                    borderRadius: 2,
                                                                    fontSize: {  xl: '14px', lg: '12px', md: '11px' }
                                                                }}
                                                                alignSelf="center"
                                                                alignItems="center"
                                                                index={id}
                                                                onClick={() => {
                                                                    changeSingleChoiceOption(code, "obstetricHistories", index, "operativeProcedure", item)

                                                                }}
                                                            >
                                                                {item.label}
                                                            </Typography>
                                                        ))}
                                                    </Grid>
                                                </Grid>

                                                <Grid size={{ xs: 12, md: 12 }} mt={1} >
                                                    <Grid container>
                                                        <Grid size={{ xs: 12, md: 4 }}   >
                                                            <Typography variant='body1' textAlign={'left'} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }}>Complication in procedure</Typography>
                                                            <Grid sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>
                                                                <Typography sx={{ width: '45%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasComplicationProcedure ? '1px solid #919191' : 'none', borderRadius: 2, p:0.4 }}
                                                                    onClick={() => {
                                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "hasComplicationProcedure", true)
                                                                    }}>
                                                                    Yes
                                                                </Typography>
                                                                <Typography sx={{ width: '45%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasComplicationProcedure == false ? '1px solid #919191' : 'none', borderRadius: 2, p:0.4 }} onClick={() => {
                                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "hasComplicationProcedure", false)
                                                                }}>
                                                                    No
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 4 }}   >
                                                            <Typography variant='body1' textAlign={'left'} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }}>Blood Transfusion</Typography>

                                                            <Grid sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>
                                                                <Typography sx={{ width: '45%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasBloodTransfusion ? '1px solid #919191' : 'none', borderRadius: 2, p:0.4 }}
                                                                    onClick={() => {
                                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "hasBloodTransfusion", true)
                                                                    }}>
                                                                    Yes
                                                                </Typography>
                                                                <Typography sx={{ width: '45%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasBloodTransfusion == false ? '1px solid #919191' : 'none', borderRadius: 2, p:0.4  }} onClick={() => {
                                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "hasBloodTransfusion", false)
                                                                }}>
                                                                    No
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 4 }}   >
                                                            <Typography variant='body1' textAlign={'left'} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }}>Given Anesthesia</Typography>
                                                            <Grid sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'row' }, gap: { xs: 0.5, md: 0 } }}>
                                                                <Typography sx={{ width: '40%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasUsedAnesthesia ? '1px solid #919191' : 'none', borderRadius: 2, p:0.4  }}
                                                                    onClick={() => {
                                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "hasUsedAnesthesia", true)
                                                                    }}>
                                                                    Yes
                                                                </Typography>
                                                                <Typography sx={{ width: '40%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasUsedAnesthesia == false ? '1px solid #919191' : 'none', borderRadius: 2, p:0.4  }} onClick={() => {
                                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "hasUsedAnesthesia", false)
                                                                    handleChangeObstetricsData(code, "obstetricHistories", index, "anesthesiaTypes", [])
                                                                }}>
                                                                    No
                                                                </Typography>
                                                            </Grid>

                                                            {historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasUsedAnesthesia && <Grid container sx={{}}  >
                                                                <Grid sx={{ display: 'flex', flexDirection: 'row', }} size={{ xs: 1, md: 2 }}>
                                                                    <Box sx={{ display: { xs: 'none', md: 'inline-block' }, lineHeight: 0 }}><svg xmlns="http://www.w3.org/2000/svg"
                                                                        width="60"
                                                                        height="40"
                                                                        viewBox="0 0 150 200"
                                                                    >
                                                                        <path
                                                                            d="M10 100 Q 180 0, 190 180"
                                                                            stroke="black"
                                                                            strokeWidth="5"
                                                                            fill="transparent"
                                                                            transform="rotate(200 100 100)"
                                                                        />
                                                                    </svg></Box>
                                                                </Grid>

                                                                <Grid sx={{ display: 'flex', flexWrap: 'wrap', }} mt={1} size={{ xs: 1, md: 10 }} justifySelf={"flex-end"} >
                                                                    {config?.anesthesiaTypes?.map((item, id) => (
                                                                        <Typography
                                                                            sx={{
                                                                                textAlign: 'center',
                                                                                // fontSize: '14px',
                                                                                cursor: 'pointer',
                                                                                border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.["anesthesiaTypes"]?.find((indication) => indication.label === item.label) ? '1px solid #919191' : 'none',
                                                                                p: 0.5,
                                                                                m: 0.5,
                                                                                borderRadius: 2,
                                                                                fontSize: { xl: '14px', lg: '12px', md: '11px' }
                                                                            }}
                                                                            alignSelf="center"
                                                                            alignItems="center"
                                                                            index={id}
                                                                            onClick={() => {
                                                                                changeSingleChoiceOption(code, "obstetricHistories", index, "anesthesiaTypes", item)
                                                                            }}
                                                                        >
                                                                            {item.label}
                                                                        </Typography>
                                                                    ))}
                                                                </Grid>
                                                            </Grid>}
                                                        </Grid>
                                                    </Grid>
                                                </Grid>


                                            </Grid>

                                        </Grid>

                                        <Grid size={{ xs: 12, md: 12 }} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)', p: 1, borderRadius: 5, }}>
                                            <Grid container mt={1}>
                                                <Grid size={{ xs: 12, md: 4 }}   >
                                                    <Typography variant='body1' textAlign={'left'} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }} >Intra & Postpartum complication </Typography>
                                                </Grid>

                                                <Grid sx={{ display: 'flex', flexDirection: 'row', }} size={{ xs: 12, md: 8 }} justifyContent={"flex-end"} >
                                                    <Typography sx={{ width: '25%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasAncComplications ? '1px solid #919191' : 'none', borderRadius: 2, display: 'flex', height: '18px', justifyContent: 'center', alignItems: 'center',p:0.5 }}
                                                        onClick={() => {
                                                            handleChangeObstetricsData(code, "obstetricHistories", index, "hasAncComplications", true)


                                                        }}>
                                                        Yes
                                                    </Typography>
                                                    <Typography sx={{ width: '25%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasAncComplications == false ? '1px solid #919191' : 'none', borderRadius: 2, display: 'flex', height: '18px', justifyContent: 'center', alignItems: 'center',p:0.5 }} onClick={() => {
                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "hasAncComplications", false)
                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "ancComplications", [])

                                                    }}>
                                                        No
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 1 }} >
                                                </Grid>

                                                {
                                                    historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasAncComplications && <>
                                                        <Grid size={{ xs: 12, md: 8 }} mt={1} sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }} justifySelf={"flex-end"} justifyContent={"flex-end"}>
                                                            {config?.intraPostpartumComplications?.map((item, id) => (
                                                                <Typography
                                                                    sx={{
                                                                        // fontSize: '14px',
                                                                        cursor: 'pointer',
                                                                        border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.["ancComplications"]?.find((indication) => indication.label === item.label) ? '1px solid #919191' : 'none',
                                                                        p: 0.5,
                                                                        m: 0.5,
                                                                        borderRadius: 2,
                                                                        fontSize: { xl: '14px', lg: '12px', md: '11px' }
                                                                    }}
                                                                    index={id}
                                                                    onClick={() => {
                                                                        changeSingleChoiceOption(code, "obstetricHistories", index, "ancComplications", item)
                                                                    }}
                                                                >
                                                                    {item.label}
                                                                </Typography>
                                                            ))}
                                                        </Grid>

                                                        <Box sx={{ display: { xs: 'none', md: 'inline-block' }, lineHeight: 0 }}><svg xmlns="http://www.w3.org/2000/svg"
                                                            width="30"
                                                            height="40"
                                                            viewBox="0 0 150 200"

                                                        >
                                                            <path
                                                                d="M10 100 Q 180 0, 190 180"
                                                                stroke="black"
                                                                strokeWidth="5"
                                                                fill="transparent"
                                                                transform="rotate(105 100 100)"
                                                            />
                                                        </svg></Box>
                                                    </>
                                                }
                                            </Grid>
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 12 }} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)', p: 1, borderRadius: 5, }}>
                                            <Grid container mt={1}>
                                                <Grid size={{ xs: 12, md: 4 }}   >
                                                    <Typography variant='body1' textAlign={'left'} sx={{ fontSize: { xl: '12px', lg: '11px', md: '10px' } }} >During and After Delivery Complications </Typography>
                                                </Grid>

                                                <Grid sx={{ display: 'flex', flexDirection: 'row', }} size={{ xs: 12, md: 8 }} justifyContent={"flex-end"} >
                                                    <Typography sx={{ width: '25%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasDuringAfterDeliveryComplications ? '1px solid #919191' : 'none', borderRadius: 2, display: 'flex', height: '18px', justifyContent: 'center', alignItems: 'center',p:0.5 }}
                                                        onClick={() => {
                                                            handleChangeObstetricsData(code, "obstetricHistories", index, "hasDuringAfterDeliveryComplications", true)


                                                        }}>
                                                        Yes
                                                    </Typography>
                                                    <Typography sx={{ width: '25%', fontSize: { xl: '14px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasDuringAfterDeliveryComplications == false ? '1px solid #919191' : 'none', borderRadius: 2, display: 'flex', height: '18px', justifyContent: 'center', alignItems: 'center',p:0.5 }} onClick={() => {
                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "hasDuringAfterDeliveryComplications", false)
                                                        handleChangeObstetricsData(code, "obstetricHistories", index, "duringAfterDeliveryComplications", [])

                                                    }}>
                                                        No
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 2 }} >
                                                </Grid>

                                                {
                                                    historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.hasDuringAfterDeliveryComplications && <>
                                                        <Grid size={{ xs: 12, md: 7 }} mt={1}>
                                                            <Grid sx={{ display: 'flex', flexWrap: 'wrap', }} >
                                                                {config?.duringAndAfterDeliveryComplications?.map((item, id) => (
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize: '14px',
                                                                            cursor: 'pointer',
                                                                            border: historyBlocks?.[code]?.data?.obstetricHistories?.[index]?.["duringAfterDeliveryComplications"]?.find((indication) => indication.label === item.label) ? '1px solid #919191' : 'none',
                                                                            p: 0.5,
                                                                            m: 0.5,
                                                                            borderRadius: 2,
                                                                            fontSize: { xl: '14px', lg: '12px', md: '11px' }
                                                                        }}
                                                                        index={id}
                                                                        onClick={() => {
                                                                            changeSingleChoiceOption(code, "obstetricHistories", index, "duringAfterDeliveryComplications", item)
                                                                        }}
                                                                    >
                                                                        {item.label}
                                                                    </Typography>
                                                                ))}
                                                            </Grid>
                                                        </Grid>

                                                        <Box sx={{ display: { xs: 'none', md: 'inline-block' }, lineHeight: 0 }}><svg xmlns="http://www.w3.org/2000/svg"
                                                            width="30"
                                                            height="40"
                                                            viewBox="0 0 150 200"

                                                        >
                                                            <path
                                                                d="M10 100 Q 180 0, 190 180"
                                                                stroke="black"
                                                                strokeWidth="5"
                                                                fill="transparent"
                                                                transform="rotate(105 100 100)"
                                                            />
                                                        </svg></Box>
                                                    </>
                                                }
                                            </Grid>
                                        </Grid>

                                    </Grid>
                                </Grid>
                            </Grid>
                                : <ObstetricHistoryCard savedPastHistory={historyBlocks?.[code]?.data} index={index} label="Term Birth" />
                })
            }
        </Grid>
    }

    const updateHistoryObject = (code, field, object) => {
        setHistoryBlocks(prev => ({
            ...prev,
            [code]: {
                ...prev[code],
                data: {
                    ...(prev[code].data ?? {}),
                    [field]: object
                }
            }
        }));
    };

    const updateHistoryNestedObject = (
        code,
        field,
        children
    ) => {
        setHistoryBlocks(prev => ({
            ...prev,
            [code]: {
                ...prev[code],
                data: {
                    ...prev[code].data,
                    [field]: {
                        ...prev[code].data[field],
                        children
                    }
                }
            }
        }));
    };

    const renderContent = (config, code) => {
        return <Grid item sx={{ padding: 1, pb: 2 }}>

            {patientType !== "GYNECOLOGY" && (
                <Grid container sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xl: '17px', lg: '16px', md: '13px', xs: '14px' } }}>
                        {patientReducer?.patientInfo?.lmp ? moment(patientReducer?.patientInfo?.lmp).format('DD MMM YYYY') : null}
                    </Typography>
                </Grid>
            )}

            {(historyBlocks?.['OBSTETRIC']?.data?.gtpal?.gravida  > 1 && patientType !== "GYNECOLOGY") && <Grid container spacing={1} sx={{ p: 1 }}>
                <Grid size={{ xs: 7, md: 3 }} sx={{ alignSelf: 'center' }}>
                    <Typography textAlign={"left"} sx={{ fontSize: { xl: '13px', lg: '12px', md: '12px' } }}>Inter Pregnancy Interval</Typography>
                </Grid>
                <Grid size={{ xs: 5, md: 5 }} sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                        <CustomSelectBox
                            value={historyBlocks?.[code]?.data?.timeToConceiveMonths || ''}
                            handleChange={(e) => {
                                updateHistoryBlock(
                                    code,
                                    ["timeToConceiveMonths"],
                                    parseInt(e)
                                )
                            }}
                            placeholder="MM"
                            options={interPregnancyMonths}
                            width={{ xl: '50px', lg: '45px', md: '30px' }}
                            selectStyle={{
                                height: '25px',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            edit={edit}
                        />
                        <CustomSelectBox
                            value={historyBlocks?.[code]?.data?.timeToConceiveYears || ''}
                            handleChange={(e) => {
                                updateHistoryBlock(
                                    code,
                                    ["timeToConceiveYears"],
                                    parseInt(e)
                                )
                            }}
                            placeholder="YY"
                            options={interPregnancyYears}
                            width={{ xl: '50px', lg: '45px', md: '40px' }}
                            selectStyle={{
                                height: '25px',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                            edit={edit}
                        />

                </Grid>
            </Grid>}
            {patientType !== "GYNECOLOGY" && <Grid container spacing={1} sx={{ p: 1, }}>
                <Grid size={{ xs: 5, md: 3 }} sx={{ alignSelf: 'center' }}>
                    <Typography textAlign={"left"} sx={{ fontSize: { xl: '13px', lg: '12px', md: '12px' } }}>Pregnancy</Typography>
                </Grid>
                <Grid size={{ xs: 7, md: 5 }}>
                    {
                        edit || (!edit && historyBlocks?.[code]?.data?.conceptionMethod?.value) ? <Box sx={{ mt: 1 }}>
                            {/* Level 1: Natural / Assisted */}
                            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 0.5 }}>
                                {config?.conceptionMethod.map(item => (
                                    <Typography key={item.value} fontSize="14px" sx={{
                                        cursor: edit ? 'pointer' : 'default',
                                        border: historyBlocks?.[code]?.data?.conceptionMethod?.value == item.value ? '1px solid #919191' : 'none',
                                        borderRadius: '5px', p: 0.5, minWidth: 60,
                                        fontSize: { xl: '14px', lg: '12px', md: '10px' }
                                    }}
                                        onClick={() => edit && updateHistoryObject(
                                            code,
                                            "conceptionMethod",
                                            {
                                                label: item.label,
                                                value: item.value,
                                                children: []
                                            }
                                        )}

                                    >
                                        {item.label}
                                    </Typography>
                                ))}
                            </Box>

                            {/* Level 2: Self / Donor (only when Assisted) */}
                            {historyBlocks?.[code]?.data?.conceptionMethod?.value == "ASSISTED" && (
                                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 0.5, mt: 0.5, pl: { xs: 1, md: 2 } }}>
                                    {config?.conceptionMethod.map(item =>
                                        item.children.map((child) => (
                                            <Typography key={child.value} fontSize="14px" sx={{
                                                cursor: edit ? 'pointer' : 'default',
                                                border: historyBlocks?.[code]?.data?.conceptionMethod?.children?.find((ite) => ite.value == child.value) ? '1px solid #919191' : 'none',
                                                borderRadius: '5px', p: 0.5, minWidth: 50,
                                                fontSize: { xl: '14px', lg: '12px', md: '10px' }
                                            }}
                                                onClick={() => {
                                                    const children = [{ label: child.label, value: child.value, children: [] }];
                                                    edit &&

                                                        updateHistoryNestedObject(
                                                            code,
                                                            "conceptionMethod",
                                                            children)
                                                }}
                                            >
                                                {child.label}
                                            </Typography>
                                        ))
                                    )}
                                </Box>
                            )}

                            {/* Level 3: Male / Female / Embryo (only when Donor) */}
                            {historyBlocks?.[code]?.data?.conceptionMethod?.value == "ASSISTED" && historyBlocks?.[code]?.data?.conceptionMethod?.children?.find((val) => val.value == "DONOR") && (
                                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 0.5, mt: 0.5, pl: { xs: 2, md: 3 } }}>
                                    {config?.conceptionMethod.map(item =>
                                        item.children.map((child) =>
                                            child.children.map((nestedChild) => (
                                                <Typography key={nestedChild.label} fontSize="14px" sx={{
                                                    cursor: edit ? 'pointer' : 'default',
                                                    border: historyBlocks?.[code]?.data?.conceptionMethod?.children?.find((val) => val.value == "DONOR").children.find((it) => it.label == nestedChild.label) ? '1px solid #919191' : 'none',
                                                    borderRadius: '5px', p: 0.5, minWidth: 50,
                                                    fontSize: { xl: '14px', lg: '11px', md: '9px' }
                                                }}
                                                    onClick={() => {
                                                        const updatedTree = updateDonorChildren(historyBlocks?.[code]?.data?.conceptionMethod?.children, "DONOR", [{ ...nestedChild }]);
                                                        edit && updateHistoryNestedObject(
                                                            code,
                                                            "conceptionMethod",
                                                            updatedTree)
                                                    }}
                                                >
                                                    {nestedChild.label}
                                                </Typography>
                                            ))
                                        )
                                    )}
                                </Box>
                            )}
                        </Box>
                            :
                            <Typography textAlign={'start'}>
                                -
                            </Typography>
                    }
                </Grid>
            </Grid>}
            {console.log("historyBlocks", historyBlocks)}
            {<Grid container spacing={1} mt={1} sx={{
                p: 1,
            }}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <Typography textAlign={"left"} sx={{ fontSize: { xl: '14px', lg: '13px', md: '12px' } }}>Notes</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }} sx={{ display: 'flex', flexDirection: 'row' }}>
                    <TextField
                        fullWidth
                        multiline
                        placeholder="Type Here.."
                        value={historyBlocks?.[code]?.data?.notes}
                        onChange={(e) => updateHistoryBlock(
                            code,
                            ["notes",],
                            e.target.value
                        )}
                        disabled={!edit}
                        sx={{
                            m: 0,
                            '& .MuiOutlinedInput-root': {
                                p: 0,
                                '& fieldset': {
                                    border: '1px solid #919191',
                                },
                                '& input, & textarea': {
                                    minHeight: '40px',
                                    fontSize: '12px',
                                    padding: '10px',
                                    backgroundColor: '#FFFF',
                                    borderRadius: '5px',
                                    resize: 'none'
                                },
                            },
                        }}
                    />
                </Grid>
            </Grid>}

        </Grid>
    }

    const renderField = (field, block) => {
        switch (field.inputType) {
            case "Text":
                return (
                    <CustomTextField
                        value={historyBlocks?.[block.code]?.data?.[field.key] ?? ""}
                        setValue={(val) => {
                            updateHistoryBlock(
                                block.code,
                                [field.key],
                                val
                            )
                        }}
                        edit={edit}
                    />
                );

            case "Number":
                return (
                    <CustomTextField
                        value={historyBlocks?.[block.code]?.data?.[field.key] ?? ""}
                        setValue={(val) => {
                            updateHistoryBlock(
                                block.code,
                                [field.key],
                                Number(val)
                            )
                        }}
                        edit={edit}
                    />
                );
            case "Date":
                return (
                    <TextField
                        fullWidth
                        type="date"
                        value={historyBlocks?.[block.code]?.data?.[field.key] ?? ""}
                        onChange={(e) =>
                            updateHistoryBlock(
                                block.code,
                                [field.key],
                                e.target.value
                            )
                        }
                        InputLabelProps={{ shrink: true }}
                    />
                );
            case "YesNo":
                return (
                    edit ?
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                            <Typography
                                sx={{ ...yesNoContainer, border: historyBlocks?.[block.code]?.data?.[field.key] ? '1px solid #919191' : 'none',   width: '20%', }}
                                onClick={() => {
                                    updateHistoryBlock(
                                        block?.code,
                                        [field.key],
                                        true
                                    )
                                }}
                            >
                                Yes
                            </Typography>
                            <Typography
                                sx={{ ...yesNoContainer, border: historyBlocks?.[block.code]?.data?.[field.key] == false ? '1px solid #919191' : 'none',   width: '20%', }}
                                onClick={() => {
                                    updateHistoryBlock(
                                        block?.code,
                                        [field.key],
                                        false
                                    )
                                }}
                            >
                                No
                            </Typography>
                        </Box>
                        : <Typography textAlign={'center'} sx={{ fontSize: { xl: '15px', lg: '14px', md: '13px' } }}>
                            {historyBlocks?.[block.code]?.data?.[field.key] ? "Yes" : "No"}
                        </Typography>
                );
            case "Select":
                return (

                    <TableSelBox
                        options={field.options}
                        value={historyBlocks?.[block.code]?.data?.[field.key] || ""}
                        setValue={(val) => {
                            updateHistoryBlock(
                                block.code,
                                [field.key],
                                val
                            )
                        }}
                        edit={edit}
                    />
                );
            case "MultiSelect": {
                const selectedMultiValues = historyBlocks?.[block.code]?.data?.[field.key] || [];
                return (
                    <Box sx={{ width: '100%' }}>
                        <Autocomplete
                            multiple
                            options={field.options}
                            value={selectedMultiValues}
                            onChange={(_, value) => {
                                let newValue = value;
                                if (value.length > 0) {
                                    const lastSelected = value[value.length - 1];
                                    if (lastSelected === "None") {
                                        newValue = ["None"];
                                    } else if (value.includes("None")) {
                                        newValue = value.filter(item => item !== "None");
                                    }
                                }
                                updateHistoryBlock(
                                    block.code,
                                    [field.key],
                                    newValue
                                );
                            }}
                            style={{
                                minHeight: '30px',
                                borderRadius: 5,
                                // backgroundColor: 'white',
                                // boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.25)',
                            }}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Tap to add." />
                            )}
                            disabled={!edit}
                        />
                        {field.nestedByOption && selectedMultiValues.map((selectedOpt) => {
                            if (field.nestedByOption[selectedOpt]) {
                                return (
                                    <Box key={selectedOpt} sx={{ mt: 1, p: 1, pl: 2, borderLeft: '2px solid #ccc' }}>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 1, color: '#666' }}>
                                            {selectedOpt}
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {field.nestedByOption[selectedOpt].map((nestedField, idx) => (
                                                <Grid size={{ xs: 12, md: 6 }} key={idx}>
                                                    <Typography sx={{ mb: 0.5, fontSize: '12px' }}>
                                                        {nestedField.label || nestedField.key}
                                                    </Typography>
                                                    {renderField(nestedField, block)}
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                );
                            }
                            return null;
                        })}
                    </Box>
                );
            }
            default:
                return null;
        }
    };

    const isLmpEditable = (patientReducer?.patientInfo?.lmp == null || lmpDate == null || isEditingLmp);

    const renderLmpField = () => {
        return <Grid size={{ xs: 12, md: 12 }}>
            <Grid container spacing={{ xs: 1, md: 0 }}>
                {isLmpEditable && edit ? (
                    <Grid size={{ xs: 5, xl: 1.3, lg: 1.5, md: 1.5 }} sx={{ mt: 0.5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: { xs: '13px', xl: '12px', lg: '10px', md: '9px' }, fontWeight: { xs: 600, md: 'normal' }, textAlign: 'left' }}>LMP*</Typography>

                            <Box
                                onClick={() => inputRef.current && inputRef.current.showPicker()}
                                sx={{
                                    cursor: 'pointer',
                                }}
                            >
                                <TextField
                                    inputRef={inputRef}
                                    type="date"
                                    fullWidth
                                    value={
                                        lmpDate
                                            ? moment(lmpDate).format('YYYY-MM-DD')
                                            : patientReducer?.patientInfo?.lmp ? moment(patientReducer?.patientInfo?.lmp).format('YYYY-MM-DD') : ''
                                    }
                                    onChange={(e) =>
                                        setLmpDate(e.target.value)
                                    }
                                    inputProps={{
                                        max: patientType == "GYNECOLOGY" ? moment().format('YYYY-MM-DD') :
                                            moment().subtract(28, 'days').format('YYYY-MM-DD'),
                                        min: moment()
                                            .subtract(273, 'days')
                                            .format('YYYY-MM-DD'),
                                        style: {
                                            height: '20px',
                                            padding: '3px 2px',
                                        },
                                    }}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            '& fieldset': {
                                                border: '1px solid #919191',
                                            },
                                        },
                                        '& input': {
                                            backgroundColor: '#FFF',
                                            border: 'none',
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: {
                                                xs: '13px',
                                                xl: '11px',
                                                lg: '10px',
                                                md: '7px',
                                            },
                                        },
                                    }}
                                />
                            </Box>
                            <CheckCircleOutlineIcon
                                fontSize="small"
                                sx={{ cursor: "pointer", ml: 0.5 }}
                                onClick={() => {
                                    if (!lmpDate) {
                                        showToast("Lmp is Mandatory", "info");
                                        return;
                                    }
                                    store.dispatch(patientActions.updatePatientDetailsRequestAction(patientId, { lmp: lmpDate }));
                                    setIsEditingLmp(false);
                                }}
                            />
                        </Box>
                    </Grid>
                ) : (
                    (patientReducer?.patientInfo?.lmp || lmpDate) && (
                        <Grid size={{ xs: 5, xl: 1.3, lg: 1.5, md: 1.5 }} sx={{ mt: 0.5 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: { xs: '13px', xl: '12px', lg: '10px', md: '9px' }, fontWeight: 600 }}>
                                    LMP:
                                </Typography>
                                <Typography sx={{ fontSize: { xs: '13px', xl: '12px', lg: '10px', md: '9px' } }}>
                                    {moment(lmpDate || patientReducer?.patientInfo?.lmp).format('DD MMM YYYY')}
                                </Typography>
                                {edit && (
                                    <Tooltip title="Edit LMP">
                                        <img
                                            src={require("../../assets/EditIcon.svg").default}
                                            alt="edit-lmp"
                                            style={{ cursor: 'pointer', width: '14px', height: '14px' }}
                                            onClick={() => setIsEditingLmp(true)}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        </Grid>
                    )
                )}
            </Grid>
        </Grid>;
    };

    const renderEditIcon = () => {
        return (!edit && hasPermission("HISTORY_CREATE") && mode == "edit") && <Grid size={{ xs: 12, md: 12 }} sx={{ marginTop: '10px', justifyContent: 'flex-end', display: 'flex' }}>
            <Tooltip title="Edit">
                <img
                    src={require("../../assets/EditIcon.svg").default}
                    alt="left-arrow"
                    style={{

                        cursor: 'pointer'
                    }}
                    onClick={() => {
                        setEdit(!edit)
                    }}
                />
            </Tooltip>
        </Grid>
    }

    const renderStructuredOrDynamicBlocks = () => {
        return <Box sx={{ width: "100%", mt: 1 }}>
            {blocks?.map((block) => {
                return <Accordion
                    key={block.id}
                    elevation={0}
                    disableGutters
                    sx={{
                        borderRadius: "10px",
                        border: "1px solid #E5E7EB",
                        // backgroundColor: 'rgba(238, 251, 255, 0.3)',
                        // boxShadow: '0px 4px 4px rgba(0,0,0,0.10)',
                        mb: 2,
                        "&:before": {
                            display: "none",
                        },
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>
                            {block.name}
                        </Typography>
                    </AccordionSummary>

                    <AccordionDetails>
                        {
                            block.code == "PAST_HISTORY" ?
                                <>
                                    <Grid size={{ xs: 12, md: 12 }} >
                                        <Typography variant='body1' textAlign={'left'} sx={headerLabel} >Past Disease</Typography>
                                        <Grid container sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '10px', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)' }}>
                                            <DiseaseSelector
                                                labelKey="diseaseName"
                                                valueKey="diseaseId"
                                                data={block?.config?.pastDiseases || []}
                                                savedData={historyBlocks?.[block?.code]?.data?.pastDiseases || []}
                                                onChange={(updatedList, label) => {
                                                    if (label == 'diseaseName') {
                                                        updateHistoryBlock(
                                                            block?.code,
                                                            ["pastDiseases"],
                                                            updatedList
                                                        )
                                                    }
                                                }}
                                                edit={edit}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 12 }}>
                                        <Typography variant='body1' textAlign={'left'} mt={0.5} sx={headerLabel}>Allergies</Typography>

                                        <Grid container sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '10px', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)' }}>
                                            <DiseaseSelector
                                                data={block?.config?.allergies || []}
                                                savedData={historyBlocks?.[block?.code]?.data?.patientAllergies || []}
                                                onChange={(updatedAllergies, label) => {
                                                    if (label == 'allergyName') {
                                                        updateHistoryBlock(
                                                            block?.code,
                                                            ["patientAllergies"],
                                                            updatedAllergies
                                                        )
                                                    }
                                                }}
                                                labelKey="allergyName"
                                                valueKey="allergyId"
                                                edit={edit}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 12 }} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '10px', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)', p: 1 }} mt={1}>
                                        <Grid container spacing={1} mt={1}>
                                            <Grid size={{ xs: 7, md: 3 }} sx={{ alignSelf: 'center', }}>
                                                <Typography textAlign={"left"} sx={headerLabel}>Previous Medication</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 5, md: 3 }} sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                                                {edit ? <>
                                                    <Typography
                                                        sx={{...yesNoContainer,border:historyBlocks?.[block?.code]?.data?.hasPreviousMedication ? '1px solid #919191' : 'none',}}
                                                        onClick={() => {
                                                            updateHistoryBlock(
                                                                block?.code,
                                                                ["hasPreviousMedication"],
                                                                true
                                                            )
                                                        }}
                                                    >
                                                        Yes
                                                    </Typography>
                                                    <Typography
                                                        sx={{...yesNoContainer,border:historyBlocks?.[block?.code]?.data?.hasPreviousMedication == false ? '1px solid #919191' : 'none',}}
                                                        onClick={() => {
                                                            updateHistoryBlock(
                                                                block?.code,
                                                                ["hasPreviousMedication"],
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        No
                                                    </Typography>
                                                </>
                                                    : <Typography
                                                        sx={{ width: '50%', fontSize: '12px', cursor: 'default', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                                    >
                                                        {historyBlocks?.[block?.code]?.data?.hasPreviousMedication ? 'Yes' : 'No'}
                                                    </Typography>
                                                }
                                            </Grid>
                                            {historyBlocks?.[block?.code]?.data?.hasPreviousMedication && <Grid size={{ xs: 12, md: 12 }} sx={{ justifySelf: isMobile ? 'flex-end' : 'flex-start', display: 'flex', justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
                                                <TextField
                                                    style={{
                                                        width: '40%'
                                                    }}
                                                    multiline
                                                    placeholder="Type Here"
                                                    value={historyBlocks?.[block?.code]?.data?.previousMedicationDetails}
                                                    onChange={(e) => updateHistoryBlock(
                                                        block?.code,
                                                        ["previousMedicationDetails"],
                                                        e.target.value
                                                    )
                                                    }
                                                    disabled={!edit}
                                                    sx={{
                                                        m: 0,
                                                        '& .MuiOutlinedInput-root': {
                                                            p: 0,
                                                            '& fieldset': {
                                                                border: 'none',
                                                            },
                                                            '& input, & textarea': {
                                                                minHeight: '8px',
                                                                fontSize: '12px',
                                                                padding: '10px',
                                                                backgroundColor: '#F2F2F2',
                                                                borderRadius: '5px',
                                                                resize: 'none'
                                                            },
                                                        },
                                                    }}
                                                />
                                            </Grid>}
                                        </Grid>
                                        <Grid container spacing={1} mt={1}>
                                            <Grid size={{ xs: 7, md: 3 }} sx={{ alignSelf: 'center' }}>
                                                <Typography textAlign={"left"} sx={headerLabel}>Dentures</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 5, md: 3 }} sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                                                {edit ?
                                                    <>
                                                        <Typography
                                                            sx={{...yesNoContainer, border: historyBlocks?.[block?.code]?.data?.hasDentures ? '1px solid #919191' : 'none'}}
                                                            onClick={() => {
                                                                updateHistoryBlock(
                                                                    block?.code,
                                                                    ["hasDentures"],
                                                                    true
                                                                )
                                                            }}
                                                        >
                                                            Yes
                                                        </Typography>
                                                        <Typography 
                                                            sx={{...yesNoContainer, border: historyBlocks?.[block?.code]?.data?.hasDentures== false ? '1px solid #919191' : 'none'}}
                                                            onClick={() => {
                                                                updateHistoryBlock(
                                                                    block?.code,
                                                                    ["hasDentures"],
                                                                    false
                                                                )
                                                            }}
                                                        >
                                                            No
                                                        </Typography>
                                                    </>
                                                    :
                                                    <Typography sx={{ width: '50%', fontSize: '12px', cursor: 'default', backgroundColor: "#FFFFFF", borderRadius: '5px', display: 'flex', height: '18px', justifyContent: 'center', alignItems: 'center' }}

                                                    >
                                                        {historyBlocks?.[block?.code]?.data?.hasDentures ? 'Yes' : "No"}
                                                    </Typography>
                                                }
                                            </Grid>
                                        </Grid>

                                        <Grid container spacing={1} mt={1}>
                                            <Grid size={{ xs: 7, md: 3 }} sx={{ alignSelf: 'center' }}>
                                                <Typography textAlign={"left"} sx={headerLabel}>Contact Lenses</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 5, md: 3 }} sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                                                {edit ? <>
                                                    <Typography
                                                        sx={{ ...yesNoContainer, border: historyBlocks?.[block?.code]?.data?.hasContactLense ? '1px solid #919191' : 'none' }}
                                                        onClick={() => {
                                                            updateHistoryBlock(
                                                                block?.code,
                                                                ["hasContactLense"],
                                                                true
                                                            )
                                                        }}
                                                    >
                                                        Yes
                                                    </Typography>
                                                    <Typography
                                                        sx={{ ...yesNoContainer, border: historyBlocks?.[block?.code]?.data?.hasContactLense == false ? '1px solid #919191' : 'transparent' }}
                                                        onClick={() => {
                                                            updateHistoryBlock(
                                                                block?.code,
                                                                ["hasContactLense"],
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        No
                                                    </Typography>
                                                </>
                                                    :
                                                    <Typography sx={{ width: '50%', fontSize: '12px', cursor: 'default', backgroundColor: "#FFFFFF", borderRadius: '5px', display: 'flex', height: '18px', justifyContent: 'center', alignItems: 'center' }}

                                                    >
                                                        {historyBlocks?.[block?.code]?.data?.hasContactLense ? 'Yes' : 'No'}
                                                    </Typography>
                                                }
                                            </Grid>
                                        </Grid>

                                        <Grid container spacing={1} mt={1}>
                                            <Grid size={{ xs: 7, md: 3 }} sx={{ alignSelf: 'center' }}>
                                                <Typography textAlign={"left"} sx={headerLabel}>Allergic reaction to any medication</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 5, md: 3 }} sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                                                {edit ? <>
                                                    <Typography
                                                        sx={{ ...yesNoContainer, border: historyBlocks?.[block?.code]?.data?.hasAllergicReaction ? '1px solid #919191' : 'none' }}
                                                        onClick={() => {
                                                            updateHistoryBlock(
                                                                block?.code,
                                                                ["hasAllergicReaction"],
                                                                true
                                                            )
                                                        }}>
                                                        Yes
                                                    </Typography>
                                                    <Typography 
                                                    sx={{ ...yesNoContainer, border: historyBlocks?.[block?.code]?.data?.hasAllergicReaction == false ? '1px solid #919191' : 'transparent'}}
                                                    onClick={() => {
                                                        updateHistoryBlock(
                                                            block?.code,
                                                            ["hasAllergicReaction"],
                                                            false
                                                        )
                                                    }}>
                                                        No
                                                    </Typography>
                                                </>
                                                    :
                                                    <Typography sx={{ width: '50%', fontSize: '12px', cursor: 'default', backgroundColor: "#FFFFFF", borderRadius: '5px', display: 'flex', height: '18px', justifyContent: 'center', alignItems: 'center' }}>
                                                        {historyBlocks?.[block?.code]?.data?.hasAllergicReaction ? 'Yes' : 'No'}
                                                    </Typography>
                                                }
                                            </Grid>

                                            {historyBlocks?.[block?.code]?.data?.hasAllergicReaction && <Grid size={{ xs: 12, md: 12 }} sx={{ justifySelf: isMobile ? 'flex-end' : 'flex-start', display: 'flex', justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
                                                <TextField
                                                    style={{
                                                        width: '40%'
                                                    }}
                                                    multiline
                                                    placeholder="Type here"
                                                    disabled={!edit}
                                                    value={historyBlocks?.[block?.code]?.data?.allergicReactionDetails}
                                                    onChange={(e) => updateHistoryBlock(
                                                        block?.code,
                                                        ["allergicReactionDetails"],
                                                        e.target.value
                                                    )
                                                    }
                                                    sx={{
                                                        m: 0,
                                                        '& .MuiOutlinedInput-root': {
                                                            p: 0,
                                                            '& fieldset': {
                                                                border: 'none',
                                                            },
                                                            '& input, & textarea': {
                                                                minHeight: '8px',
                                                                fontSize: '12px',
                                                                padding: '10px',
                                                                backgroundColor: '#F2F2F2',
                                                                borderRadius: '5px',
                                                                resize: 'none'
                                                            },
                                                        },
                                                    }}
                                                />
                                            </Grid>}
                                        </Grid>

                                        <Grid container spacing={1} mt={1}>
                                            <Grid size={{ xs: 7, md: 3 }} sx={{ alignSelf: 'center' }}>
                                                <Typography textAlign={"left"} sx={headerLabel}>Current Medication</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 5, md: 3 }} sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                                                {edit ? <>
                                                    <Typography
                                                        sx={{ ...yesNoContainer, border: historyBlocks?.[block?.code]?.data?.hasCurrentMedication ? '1px solid #919191' : 'transparent' }}
                                                        onClick={() => {
                                                            updateHistoryBlock(
                                                                block?.code,
                                                                ["hasCurrentMedication"],
                                                                true
                                                            )
                                                        }}
                                                    >
                                                        Yes
                                                    </Typography>
                                                    <Typography
                                                        sx={{ ...yesNoContainer, border: historyBlocks?.[block?.code]?.data?.hasCurrentMedication == false ? '1px solid #919191' : 'transparent' }}
                                                        onClick={() => {
                                                            updateHistoryBlock(
                                                                block?.code,
                                                                ["hasCurrentMedication"],
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        No
                                                    </Typography>
                                                </>
                                                    : <Typography
                                                        sx={{ width: '50%', fontSize: '12px', cursor: 'default', backgroundColor: "#FFFFFF", borderRadius: '5px', height: '18px', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                                    >
                                                        {historyBlocks?.[block?.code]?.data?.hasCurrentMedication ? 'Yes' : 'No'}
                                                    </Typography>
                                                }
                                            </Grid>
                                            {historyBlocks?.[block?.code]?.data?.hasCurrentMedication && <Grid size={{ xs: 12, md: 12 }} sx={{ justifySelf: isMobile ? 'flex-end' : 'flex-start', display: 'flex', justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
                                                <TextField
                                                    style={{
                                                        width: '40%'
                                                    }}
                                                    multiline
                                                    placeholder="Type Here"
                                                    value={historyBlocks?.[block?.code]?.data?.currentMedicationDetails}
                                                    onChange={(e) =>
                                                        updateHistoryBlock(
                                                            block?.code,
                                                            ["currentMedicationDetails"],
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={!edit}
                                                    sx={{
                                                        m: 0,
                                                        '& .MuiOutlinedInput-root': {
                                                            p: 0,
                                                            '& fieldset': {
                                                                border: 'none',
                                                            },
                                                            '& input, & textarea': {
                                                                minHeight: '8px',
                                                                fontSize: '12px',
                                                                padding: '10px',
                                                                backgroundColor: '#F2F2F2',
                                                                borderRadius: '5px',
                                                                resize: 'none'
                                                            },
                                                        },
                                                    }}
                                                />
                                            </Grid>}
                                        </Grid>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 12 }} mb={1} sx={{ paddingBottom: '15px' }} >
                                        {(edit || (!edit && historyBlocks[block?.code]?.data?.familyHistories?.length > 0)) && <Typography variant='body1' textAlign={'left'} m={1} sx={headerLabel}>Family History</Typography>}

                                        {edit && <Grid container spacing={0.5} sx={{ backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '10px', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)' }}>
                                            <Grid sx={{ display: 'flex', padding: '0.5rem', flexWrap: 'wrap' }}>
                                                {familyHistoryList?.map((item, index) => (
                                                    <Typography
                                                        sx={{ textAlign: 'center', fontSize: { xl: '13px', lg: '12px', md: '11px' }, cursor: 'pointer', border: historyBlocks[block?.code]?.data?.familyHistories?.find((history) => history.diseaseName === item.name) ? '1px solid #919191' : 'none',p: 0.6, m: 0.5, borderRadius: 2 }}
                                                        alignSelf="center"
                                                        alignItems="center"
                                                        onClick={() => {
                                                            const diseases = historyBlocks[block.code]?.data?.familyHistories || [];
                                                            const exists = diseases.some(d => d.diseaseId === item.id);
                                                            const updated = exists
                                                                ? diseases.filter(d => d.diseaseId !== item.id)
                                                                : [
                                                                    ...diseases,
                                                                    {
                                                                        diseaseId: item.id,
                                                                        diseaseName: item.name,
                                                                        familyMembers: [],
                                                                    },
                                                                ];
                                                            updateHistoryBlock(block.code, ["familyHistories"], updated);
                                                        }}
                                                    >
                                                        {item.name}
                                                    </Typography>
                                                ))}

                                                <CreatableSelect
                                                    placeholder="+ Add"
                                                    options={
                                                        familyHistoryList?.map((item) => ({
                                                            label: item.name,
                                                            value: item.id,
                                                            ...item
                                                        })) || []
                                                    }
                                                    onChange={(selectedOption) => {
                                                        handleFamilyHistory({
                                                            id: selectedOption?.value,
                                                            name: selectedOption?.label,

                                                        }, block?.code);
                                                    }}
                                                    onCreateOption={(inputValue) => {
                                                        handleFamilyHistory({
                                                            name: inputValue,
                                                            __isNew__: true
                                                        }, block.code);
                                                    }}
                                                    menuPlacement="top"
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        container: (base) => ({
                                                            ...base, minWidth: "120px", height: "26px",
                                                            flexShrink: 0, fontSize,
                                                        }),
                                                        control: (base) => ({ ...base, height: "26px", minHeight: "26px", border: 'none', borderRadius: '10px', boxShadow: "none", fontSize, backgroundColor: '#F2F2F2' }),
                                                        valueContainer: (base) => ({ ...base, padding: "0 6px", fontSize, }),
                                                        indicatorsContainer: (base) => ({ ...base, height: "26px", fontSize, }),
                                                        option: (base, state) => ({
                                                            ...base,
                                                            backgroundColor: state.isSelected
                                                                ? "grey"
                                                                : state.isFocused
                                                                    ? "#D3D3D3"
                                                                    : "transparent",
                                                            color: state.isSelected ? "#fff" : "black",
                                                            cursor: "pointer",
                                                        }),
                                                    }}
                                                />

                                            </Grid>
                                            {
                                                edit && historyBlocks[block.code]?.data?.familyHistories?.map((dieaseHistory) => {
                                                    return <Grid container spacing={0} mt={0.5} ref={(el) => {
                                                        if (el) diseaseRefs.current[dieaseHistory.diseaseName] = el;
                                                    }}>
                                                        <Grid size={{ xs: 12, md: 12 }} justifyItems={"center"} >
                                                            <Typography sx={{ fontSize: { xl: '14px', lg: '12px', md: '11px' }, border: '1px solid #919191', borderRadius: 2, p: 0.5, display: 'inline-block', px: 2 }} textAlign={'center'}>
                                                                {dieaseHistory.diseaseName}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 6 }} sx={{ justifyContent: 'flex-end', display: { xs: 'none', md: 'flex' } }}>
                                                            <Box sx={{ display: { xs: 'none', md: 'inline-block' }, lineHeight: 0 }}><svg xmlns="http://www.w3.org/2000/svg"
                                                                width="20"
                                                                height="20"
                                                                viewBox="50 80 140 150"
                                                            >
                                                                <path
                                                                    d="M10 120 Q 100 0, 130 140"
                                                                    stroke="black"
                                                                    strokeWidth="10"
                                                                    fill="transparent"
                                                                    transform="rotate(220 100 100)"
                                                                />
                                                            </svg></Box>
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 6 }}  >
                                                            <Typography textAlign={{ xs: 'center', md: 'left' }} sx={{ fontSize: { xs: '13px', xl: '12px', lg: '11px', md: '10px' }, mt: { xs: 0.5, md: 0 } }}>
                                                                Who in the family?
                                                            </Typography>
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 12 }} mt={0.5} sx={{ ml: { xs: 0, md: 2 } }} >
                                                            <Grid container spacing={1}>
                                                                {Object.keys(block?.config?.familyMemberTypes)?.map((group) => {
                                                                    return <Grid size={{ xs: 6, sm: 6, md: 3 }} justifyContent={'flex-start'} justifySelf={"flex-start"} justifyItems={"flex-start"}>
                                                                        <Typography sx={{ backgroundColor: "#DDEEFD", p: 0.4, borderRadius: 2, fontSize: { xl: '12px', lg: '11px', md: '10px' }, }}   >
                                                                            {group}
                                                                        </Typography>
                                                                        {block?.config?.familyMemberTypes?.[group]?.map((member) => {
                                                                            const findMember = dieaseHistory.familyMembers.find((fm) => (fm.familyMemberGroup === group && fm.familyMember === member))
                                                                            return <Typography sx={{ fontSize: { xl: '12px', lg: '11px', md: '9px' }, p: 0.5, borderRadius: 2, border: findMember ? '1px solid #919191' : "transparent", cursor: 'pointer' }}
                                                                                onClick={() => {
                                                                                    const diseases = [...(historyBlocks[block?.code]?.data?.familyHistories || [])];
                                                                                    const diseaseIndex = diseases.findIndex(
                                                                                        d => d.diseaseId === dieaseHistory.diseaseId
                                                                                    );
                                                                                    if (diseaseIndex !== -1) {
                                                                                        const members = [...diseases[diseaseIndex].familyMembers];
                                                                                        const index = members.findIndex(
                                                                                            m =>
                                                                                                m.familyMemberGroup === group &&
                                                                                                m.familyMember === member
                                                                                        );
                                                                                        if (index === -1) {
                                                                                            members.push({
                                                                                                familyMemberGroup: group,
                                                                                                familyMember: member,
                                                                                            });
                                                                                        } else {
                                                                                            members.splice(index, 1);
                                                                                        }
                                                                                        diseases[diseaseIndex].familyMembers = members;
                                                                                        updateHistoryBlock(block.code, ["familyHistories"], diseases);
                                                                                    }
                                                                                }}
                                                                                mt={0.5}
                                                                            >
                                                                                {formatText(member)}
                                                                            </Typography>
                                                                        })}
                                                                    </Grid>
                                                                })}
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                })
                                            }
                                        </Grid>}
                                        {
                                            !edit && historyBlocks[block.code]?.data?.familyHistories?.length > 0 &&
                                            <Grid container spacing={0.5} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '10px', boxShadow: '0px 4px 4px rgba(0,0,0,0.1)' }}>
                                                <Grid size={{ xs: 12, md: 12 }} >
                                                    <Grid container mt={1} >
                                                        {
                                                            historyBlocks[block.code]?.data?.familyHistories?.map((family) => {
                                                                return <Grid size={{ md: 12 }}>
                                                                    <Grid container mb={1}>
                                                                        <Grid size={{ md: 3.5 }} alignSelf={'center'}>
                                                                            <Typography textAlign={'left'} px={1} sx={{ fontSize: { xl: '12px', lg: '12px', md: '11px' } }}>
                                                                                {family.diseaseName}
                                                                            </Typography>
                                                                        </Grid>

                                                                        <Grid size={{ md: 8.5 }} sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>

                                                                            {family?.familyMembers?.map((member) => {
                                                                                return <Typography sx={{ p:0.5, borderRadius: 2, border: '1px solid #919191', mx: 0.3, fontSize: { xl: '11px', lg: '10px', md: '9px' } }}
                                                                                    mt={0.5}
                                                                                >
                                                                                    {member.familyMember.includes('_') ? formatText(member.familyMemberGroup) + " "
                                                                                        + formatText(member.familyMember) : formatText(member.familyMember)}
                                                                                </Typography>

                                                                            })}
                                                                        </Grid>

                                                                    </Grid>
                                                                </Grid>

                                                            })
                                                        }
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        }
                                    </Grid>
                                </> : block.code == "MENSTRUAL" ?
                                    <Grid size={{ xs: 12, md: 12 }} sx={{ borderRadius: 4, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' }, marginBottom: '10px' }}>
                                        <Box sx={boxContainer}>
                                            {showMenstrualHistory(block?.config, block.code)}
                                        </Box>
                                    </Grid> : block.code == "CURRENT_PREGNANCY" ?
                                        <Grid size={{ xs: 12, md: 12 }} sx={gridContainer}>
                                            <Box sx={boxContainer}>
                                                {renderContent(block?.config, block.code)}
                                            </Box>
                                        </Grid>
                                        :
                                        block.code == "OBSTETRIC" ?
                                            <Grid size={{ xs: 12, md: 12 }} sx={{ borderRadius: 4, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' }, marginBottom: '10px' }}>
                                                <Box sx={boxContainer}>
                                                    {showObstetricsHistory(block?.config, block?.code)}
                                                </Box>
                                            </Grid>
                                            : block.storageType == "DYNAMIC" ?
                                                // <Grid size={{ xs: 12, md: 12 }} sx={{ boxShadow: '0px 4px 4px rgba(0,0,0,0.25)', borderRadius: 4, backgroundColor: 'rgba(238, 251, 255, 0.3)', }}>
                                                //     {block.fields.map((field) => (
                                                //         <Grid key={field.key} size={{ xs: 12, md: 12 }}>
                                                //             {renderField(field)}
                                                //         </Grid>
                                                //     ))}
                                                // </Grid>
                                                <Grid
                                                    container
                                                    spacing={2}
                                                    sx={{
                                                        // boxShadow: '0px 4px 4px rgba(0,0,0,0.25)',
                                                        borderRadius: 4,
                                                        // backgroundColor: 'rgba(238, 251, 255, 0.3)',
                                                        p: 2,
                                                    }}
                                                >
                                                    {block.fields.map((field) => (
                                                        <Grid key={field.key} size={{ xs: 12, md: field.nestedByOption ? 12 : 4 }}>
                                                            <Grid container spacing={1}>
                                                                {/* Label */}
                                                                <Grid size={{ xs: 5, md: field.nestedByOption ? 12 : 5 }}>
                                                                    <Typography textAlign={"left"} sx={{ fontSize: { xl: '13px', lg: '12px', md: '11px' } }}>{field.label}</Typography>
                                                                </Grid>

                                                                {/* Component */}
                                                                <Grid size={{ xs: 7, md: field.nestedByOption ? 12 : 7 }}>
                                                                    {renderField(field, block)}
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                                : null
                        }
                    </AccordionDetails>
                </Accordion>
            })}
        </Box>
    }

    const renderData = () => {
        return <Grid size={{ xs: 12, md: 12 }} sx={{ height: 'auto' }}>
            <CommonGate showComplaint={true} refreshHistory={refreshHistory}/>
            <Grid container >
                {renderEditIcon()}
                {renderLmpField()}
                {renderStructuredOrDynamicBlocks()}
            </Grid>
        </Grid>
    }

    return (
        <Box sx={{ flexGrow: 1, height: { xs: 'auto', md: '70vh', lg: "70vh", xl: '60vh', },}} alignContent={'center'}>
            {!loader ? <Grid container spacing={2} sx={{ overflow: 'hidden', height: { xs: 'auto', md: '75vh', lg: "82vh", xl: '78vh', } }}>
                {isMobile ? renderData() : <Grid size={{ xs: 12, md: 12 }} sx={{ height: '100%', width: '100%', '& .custom-scrollbars__container': { height: '100%' }, '& .custom-scrollbars__content': { height: '100%', maxHeight: '100%' } }}>
                    <Scrollbar gridTemplate="auto / 1fr 20px" padding="0 1rem 1rem 0.5rem" scrollerPadding="0rem" scrollerWidth="9.3px" scrollHeight="100%">
                        {renderData()}
                    </Scrollbar>
                </Grid>}
            </Grid>
                :
                <CircularProgress />
            }
        </Box>
    );
};

export default History;
